/* Cove Package Builder JavaScript — Simple Bundles product template — 2026-08-10 */
(function () {
  'use strict';

  if (customElements.get('cove-package-builder-v2') && customElements.get('cove-package-builder')) return;

  // Storefront strings rendered by layout/theme.liquid. Every lookup keeps its
  // original English literal as the final fallback, so a missing key can never
  // blank a label.
  const PD_I18N = window.PovelleaI18n || {};
  const PDP = PD_I18N.pkg || {};
  const PDA = PD_I18N.accordion || {};
  const PDL = PD_I18N.listing || {};
  // Accordion labels only: two locale EN values differ from the historical JS
  // literals in casing ("Warranty & After-sales", "Product highlights"), so on
  // English pages the literals stay authoritative and translations apply only
  // to other locales.
  const useI18n = (document.documentElement.lang || 'en').toLowerCase() !== 'en';

  // Mirror of snippets/pv-dimension-resolved.liquid. A dimension value is
  // resolved only when every position that must hold a measurement holds one:
  // at least one digit overall, a digit-bearing token on both sides of each
  // `x` separator, and a digit-bearing token before each unit. Case, spacing
  // and the various multiplication symbols are normalised first, so none of
  // them can carry a placeholder past the check.
  // `in` is deliberately absent from the units: it is the English preposition
  // far more often than a unit here ("Available in 240 x …").
  const DIMENSION_UNITS = new Set([
    'cm', 'cms', 'centimetre', 'centimetres', 'centimeter', 'centimeters',
    'mm', 'm', 'metre', 'metres', 'meter', 'meters', 'inch', 'inches',
    'سم', 'مم', 'متر', 'بوصة'
  ]);
  // Axis labels carry no measurement of their own. The real values read
  // `W 260 x D 100 x H 78 cm`, so the token beside a separator is often the
  // next label rather than its number. The Arabic values are translated too
  // («العرض 260 × العمق 100 × الارتفاع 78 سم»), so their labels belong here as well.
  const DIMENSION_LABELS = new Set([
    'w', 'd', 'h', 'l', 'width', 'depth', 'height', 'length', 'dia', 'diameter',
    'العرض', 'عرض', 'العمق', 'عمق', 'الارتفاع', 'ارتفاع', 'الطول', 'طول',
    'القطر', 'قطر', 'المقاسات', 'الأبعاد'
  ]);
  function isResolvedDimension(raw) {
    if (typeof raw !== 'string') return false;
    const normalised = raw
      .replace(/<[^>]*>/g, ' ')
      .toLowerCase()
      .replace(/[×✕╳*]/g, ' x ')
      .replace(/[:,.;()/=]/g, ' ');
    const tokens = normalised.split(/\s+/).filter(Boolean).filter((t) => !DIMENSION_LABELS.has(t));
    if (!tokens.length) return false;
    const hasDigit = tokens.map((t) => /[0-9]/.test(t));
    if (!hasDigit.some(Boolean)) return false;
    for (let i = 0; i < tokens.length; i += 1) {
      if (tokens[i] === 'x') {
        if (i === 0 || i === tokens.length - 1) return false;
        if (!hasDigit[i - 1] || !hasDigit[i + 1]) return false;
      }
      if (DIMENSION_UNITS.has(tokens[i])) {
        if (i === 0 || !hasDigit[i - 1]) return false;
      }
    }
    return true;
  }
  window.PovelleaIsResolvedDimension = isResolvedDimension;

  // Included products are always present at least once (represented by the Simple
  // Bundles parent) and are capped at 4 total units per package instance.
  // Legacy fallback only. The real per-item base/min/max/step now arrive on each
  // card as data attributes, sourced from the povellea_package.item_quantities
  // metafield. These constants apply when a package carries no quantity data,
  // which keeps the original reference package rendering unchanged.
  const INCLUDED_MIN_QUANTITY = 1;
  const INCLUDED_MAX_QUANTITY = 4;

  class CovePackageBuilder extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.config = null;
      this.cards = [];
      this.galleryIndex = 0;
      this.galleryScrollFrame = null;
      this.galleryLastScrollTime = 0;
      this.galleryMedia = window.matchMedia('(max-width: 899px)');
      this.thumbDrag = null;
      this.thumbWasDragged = false;
      this.galleryLightbox = null;
      this.galleryLightboxViewport = null;
      this.galleryLightboxTrack = null;
      this.galleryLightboxSlides = [];
      this.galleryLightboxIndex = 0;
      this.galleryLightboxScrollFrame = null;
      this.galleryLightboxLastFocused = null;
      this.galleryLightboxCloseTimer = null;
      this.busy = false;
      this.toastTimer = null;
      this.modal = null;
      this.modalData = null;
      this.modalImages = [];
      this.modalIndex = 0;
      this.modalAnimating = false;
      this.modalPointer = null;
      this.modalLastFocused = null;
      this.modalCloseTimer = null;
      this.bodyLock = null;
      this.cartDrawer = null;
      this.cartDrawerObserver = null;
      this.mobileBar = null;
      this.mobilePurchaseEnd = null;
      this.mobileBarObserver = null;
      this.mobileBarFrame = null;
      this.preloadedImages = new Set();
      this.editPackageInstance = '';
      this.editNotice = null;
      this.editLoading = false;
      this.simpleBundlesBridge = null;
      this.simpleBundlesForm = null;
      this.simpleBundlesFields = [];
      this.simpleBundlesObserver = null;
      this.simpleBundlesRetryTimers = [];
      this.simpleBundlesReady = false;
      this.simpleBundlesSyncing = false;
    }

    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.config = this.readJson(this.querySelector('[data-package-config]')) || {};

      this.cacheElements();
      this.initializeCartDrawerScroll();
      this.initializeGallery();
      this.initializeGalleryLightbox();
      this.initializeCards();
      this.applyQuantityDataGuard();
      this.initializeSimpleBundlesBridge();
      this.initializeAccordions();
      this.initializeActions();
      this.initializeMobileBarVisibility();
      this.initializeQuickView();
      this.initializeThemeEditor();
      this.updateTotal();
      this.initializePackageEditing();
    }

    // Visible half of the fail-closed contract: lock the quantity controls and the
    // buy actions when the package's quantity data cannot be trusted.
    applyQuantityDataGuard() {
      if (this.quantityDataIsUsable()) return;
      this.setAttribute('data-quantity-data-invalid', 'true');
      this.querySelectorAll('[data-quantity-minus],[data-quantity-plus]').forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });
      this.querySelectorAll('[data-package-add],[data-package-buy]').forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });
      console.error('[Povellea package] item_quantities does not match included_products; controls disabled.');
    }

    disconnectedCallback() {
      if (!this.abortController) return;

      this.abortController.abort();
      this.abortController = null;

      if (this.galleryScrollFrame) cancelAnimationFrame(this.galleryScrollFrame);
      if (this.galleryLightboxScrollFrame) cancelAnimationFrame(this.galleryLightboxScrollFrame);
      if (this.galleryLightboxCloseTimer) clearTimeout(this.galleryLightboxCloseTimer);
      if (this.toastTimer) clearTimeout(this.toastTimer);
      if (this.modalCloseTimer) clearTimeout(this.modalCloseTimer);
      if (this.mobileBarFrame) cancelAnimationFrame(this.mobileBarFrame);
      this.closeModal(true);
      this.closeGalleryLightbox(true);
      this.cartDrawerObserver?.disconnect();
      this.cartDrawerObserver = null;
      this.mobileBarObserver?.disconnect();
      this.mobileBarObserver = null;
      this.simpleBundlesObserver?.disconnect();
      this.simpleBundlesObserver = null;
      this.simpleBundlesRetryTimers.forEach((timer) => clearTimeout(timer));
      this.simpleBundlesRetryTimers = [];

      if (this.modal) {
        this.modal.remove();
        this.modal = null;
      }
      if (this.galleryLightbox) {
        this.galleryLightbox.remove();
        this.galleryLightbox = null;
      }
      if (this.toast && !this.contains(this.toast)) this.toast.remove();

      this.cards = [];
    }

    cacheElements() {
      this.gallery = this.querySelector('[data-gallery]');
      this.galleryViewport = this.querySelector('[data-gallery-viewport]');
      this.galleryTrack = this.querySelector('[data-gallery-track]');
      this.gallerySlides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.galleryThumbs = this.querySelector('[data-gallery-thumbs]');
      this.galleryThumbButtons = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.priceOutputs = Array.from(this.querySelectorAll('[data-package-price], [data-package-total]'));
      this.originalPriceOutputs = Array.from(this.querySelectorAll('[data-package-original-price], [data-package-original-total]'));
      this.actionButtons = Array.from(this.querySelectorAll('[data-package-action]'));
      this.toast = this.querySelector('[data-package-toast]');
      this.mobileBar = this.querySelector('[data-mobile-bar]');
      this.mobilePurchaseEnd = this.querySelector('[data-mobile-purchase-end]');
    }

    initializeCartDrawerScroll() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      this.cartDrawer = drawer;

      const prepareDrawer = () => {
        drawer.querySelectorAll('cart-drawer-items, .drawer__contents, .drawer__cart-items-wrapper, .drawer__inner').forEach((element) => {
          element.setAttribute('data-lenis-prevent', '');
        });
      };

      const syncDrawerState = () => {
        prepareDrawer();
      };

      this.cartDrawerObserver = new MutationObserver(syncDrawerState);
      this.cartDrawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
      });

      this.listen(document, 'click', (event) => {
        if (!event.target.closest('#cart-icon-bubble')) return;
        window.setTimeout(syncDrawerState, 0);
      }, { capture: true });

      this.listen(drawer, 'wheel', (event) => {
        if (!drawer.classList.contains('active')) return;

        const candidates = Array.from(drawer.querySelectorAll('cart-drawer-items, .drawer__contents, .drawer__cart-items-wrapper, .drawer__inner'));
        const target = candidates
          .sort((left, right) => (right.scrollHeight - right.clientHeight) - (left.scrollHeight - left.clientHeight))
          .find((element) => element.scrollHeight > element.clientHeight + 1);
        if (target) target.scrollTop += event.deltaY;
        event.preventDefault();
        event.stopPropagation();
      }, { passive: false });

      prepareDrawer();
      syncDrawerState();
    }

    listen(target, eventName, handler, options) {
      if (!target) return;
      const eventOptions = Object.assign({}, options || {}, { signal: this.signal });
      target.addEventListener(eventName, handler, eventOptions);
    }

    readJson(node) {
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('[Cove package] Invalid JSON data.', error);
        return null;
      }
    }

    isSimpleBundlesMode() {
      return Boolean(this.config?.simpleBundlesMode && Number(this.config?.parentVariantId || 0));
    }

    normalizeBundleValue(value) {
      return String(value ?? '')
        .replace(/\u00d7/g, 'x')
        .replace(/\s+/g, ' ')
        .replace(/[\u2013\u2014]/g, '-')
        .trim()
        .toLowerCase();
    }

    rawPropertyKeyFromField(field) {
      const match = String(field?.name || '').match(/properties\[(.*?)\]/);
      return match ? String(match[1] || '') : '';
    }

    propertyKeyFromField(field) {
      return this.rawPropertyKeyFromField(field).trim();
    }

    initializeSimpleBundlesBridge() {
      if (!this.isSimpleBundlesMode()) return;

      this.simpleBundlesBridge = this.querySelector('[data-simple-bundles-bridge]');
      this.simpleBundlesForm = document.getElementById(this.config.productFormId) || this.closest('form[action*="/cart/add"]');

      const hydrate = () => {
        const scope = this.simpleBundlesForm || this;
        const fields = Array.from(scope.querySelectorAll('select[name^="properties["]'))
          .filter((field) => {
            const key = this.propertyKeyFromField(field);
            return key && !key.startsWith('_');
          });

        if (!fields.length) return false;

        this.simpleBundlesFields = fields;
        this.simpleBundlesReady = true;
        this.assignSimpleBundlesFields();
        // Dispatch real change events here: a price-locked card can default to a
        // variant that is NOT the app's first option (Lounge & Media locks the 300 CM
        // TV unit). Without the events the app widget kept its own default and the
        // cart line carried the wrong option. Events only fire when a value changes,
        // so repeated hydration is still quiet.
        this.syncAllSimpleBundlesSelections(true);
        this.updateTotal();
        return true;
      };

      hydrate();

      const observeTarget = this.simpleBundlesBridge || this.simpleBundlesForm || this;
      this.simpleBundlesObserver = new MutationObserver(() => hydrate());
      this.simpleBundlesObserver.observe(observeTarget, { childList: true, subtree: true });

      [120, 350, 800, 1500, 2500].forEach((delay) => {
        this.simpleBundlesRetryTimers.push(window.setTimeout(hydrate, delay));
      });
    }

    waitForSimpleBundlesBridge(timeout = 3500) {
      if (!this.isSimpleBundlesMode() || this.simpleBundlesReady) return Promise.resolve(this.simpleBundlesReady);

      return new Promise((resolve) => {
        const started = Date.now();
        const check = () => {
          if (this.simpleBundlesReady) {
            resolve(true);
            return;
          }
          if (Date.now() - started >= timeout) {
            resolve(false);
            return;
          }
          window.setTimeout(check, 80);
        };
        check();
      });
    }

    assignSimpleBundlesFields() {
      const unused = new Set(this.simpleBundlesFields);
      const bridgeRecords = this.cards.filter((record) => record.element.dataset.packageRole === 'included');

      bridgeRecords.forEach((record) => {
        const title = this.normalizeBundleValue(record.data?.title);
        const handle = this.normalizeBundleValue(record.data?.handle).replace(/-/g, ' ');
        let best = null;
        let bestScore = -1;

        unused.forEach((field) => {
          const key = this.normalizeBundleValue(this.propertyKeyFromField(field));
          let score = 0;
          if (key === title) score = 100;
          else if (key === handle) score = 90;
          else if (title && (key.includes(title) || title.includes(key))) score = 70;
          else if (handle && (key.includes(handle) || handle.includes(key))) score = 60;
          if (score > bestScore) {
            best = field;
            bestScore = score;
          }
        });

        if (!best || bestScore <= 0) {
          best = Array.from(unused)[0] || null;
        }

        record.simpleBundlesField = best;
        if (best) unused.delete(best);
      });
    }

    optionMatchesCandidates(option, candidates) {
      const stripPriceAdjustment = (value) => String(value || '')
        .replace(/\s*\(\s*\+[^)]*\)\s*$/, '')
        .trim();
      const rawValues = [option?.value, option?.textContent]
        .map((value) => this.normalizeBundleValue(value));
      const values = Array.from(new Set(rawValues.concat(rawValues.map(stripPriceAdjustment))));

      return candidates.some((candidate) => {
        const normalized = this.normalizeBundleValue(candidate);
        if (!normalized) return values.includes('');

        return values.some((value) => {
          if (value === normalized) return true;
          return value.endsWith(` - ${normalized}`)
            || value.endsWith(` / ${normalized}`)
            || value.endsWith(` <> ${normalized}`);
        });
      });
    }

    // Every candidate above is customer-facing text, so on a translated
    // storefront it is Arabic while the Simple Bundles <option> labels stay in
    // the shop's primary language — the app stores them as configuration, not
    // as copy, and they must never be translated. No candidate can then equal
    // any option, every select is left empty, and the app refuses the add to
    // cart. The two lists are however the same ordered variant list seen from
    // two sides, and position is not translated, so pair them by index once
    // the labels have failed. Guarded on the counts matching exactly: if the
    // app is offering a different set of variants than the card knows about,
    // the index means nothing and it is safer to stay unmatched.
    optionByVariantPosition(record, field) {
      const variants = Array.isArray(record.data?.variants) ? record.data.variants : [];
      const selectedId = Number(record.selectedVariant?.id || 0);
      if (!variants.length || !selectedId) return null;

      const selectable = Array.from(field.options || [])
        .filter((option) => String(option.value || '').trim() !== '');
      if (selectable.length !== variants.length) return null;

      const index = variants.findIndex((variant) => Number(variant.id) === selectedId);
      return index >= 0 ? selectable[index] : null;
    }

    selectedVariantCandidates(record, quantity) {
      const variant = record.selectedVariant || {};
      const options = Array.isArray(variant.options) ? variant.options.filter(Boolean) : [];
      const productTitle = String(record.data?.title || '').trim();
      const variantTitle = String(variant.title || '').trim();
      const optionSlash = options.join(' / ');
      const optionDash = options.join(' - ');
      const optionBundle = options.join(' <> ');
      const base = Array.from(new Set([
        variant.title,
        optionSlash,
        optionDash,
        optionBundle,
        options.length === 1 ? options[0] : '',
        productTitle,
        productTitle && variantTitle ? `${productTitle} - ${variantTitle}` : '',
        productTitle && optionSlash ? `${productTitle} - ${optionSlash}` : '',
        productTitle && optionDash ? `${productTitle} - ${optionDash}` : '',
        productTitle && optionBundle ? `${productTitle} - ${optionBundle}` : ''
      ].filter(Boolean)));

      if (record.element.dataset.packageRole !== 'addon') return base;
      if (quantity <= 0) {
        return ['', 'none', 'no thanks', 'not included', 'without add-on', `without ${record.data?.title || ''}`];
      }
      if (quantity === 1) return base.concat(base.map((value) => `1 x ${value}`));
      return base.flatMap((value) => [`${quantity} x ${value}`, `${value} x ${quantity}`, `${quantity}x ${value}`]);
    }

    syncSimpleBundlesSelection(record, dispatch = true) {
      if (!this.isSimpleBundlesMode() || !this.simpleBundlesReady || !record) return !this.isSimpleBundlesMode();
      if (record.element.dataset.packageRole === 'addon') return true;
      const field = record.simpleBundlesField;
      if (!field) return false;

      const quantity = record.element.dataset.packageRole === 'addon' ? Math.max(0, Number(record.quantity || 0)) : 1;
      const candidates = this.selectedVariantCandidates(record, quantity);
      const option = Array.from(field.options || []).find((candidate) => this.optionMatchesCandidates(candidate, candidates))
        || this.optionByVariantPosition(record, field);
      if (!option) return false;

      const changed = field.value !== option.value;
      field.value = option.value;
      if (dispatch && changed) {
        this.simpleBundlesSyncing = true;
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
        this.simpleBundlesSyncing = false;
      }
      return true;
    }

    updateBundleSelectionInput() {
      if (!this.simpleBundlesForm || !this.simpleBundlesFields.length) return;
      const values = this.simpleBundlesFields.map((field) => String(field.value || '').trim()).filter(Boolean);
      let hidden = this.simpleBundlesForm.querySelector('input[name="properties[_bundle_selection]"]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'properties[_bundle_selection]';
        this.simpleBundlesForm.appendChild(hidden);
      }
      hidden.value = values.join(' <> ');
    }

    syncAllSimpleBundlesSelections(dispatch = true) {
      if (!this.isSimpleBundlesMode()) return { valid: true, invalid: [] };
      if (!this.simpleBundlesReady) return { valid: false, invalid: this.cards.slice() };

      const invalid = this.cards
        .filter((record) => record.element.dataset.packageRole === 'included')
        .filter((record) => !this.syncSimpleBundlesSelection(record, dispatch));
      this.updateBundleSelectionInput();
      return { valid: invalid.length === 0, invalid };
    }

    collectSimpleBundlesProperties() {
      const properties = {};
      const scope = this.simpleBundlesForm || this;
      const fields = scope.querySelectorAll(
        'select[name^="properties"], input[name^="properties"]:checked, input[name^="properties"][type="hidden"], input[name^="properties"][type="text"]'
      );

      fields.forEach((field) => {
        const key = this.rawPropertyKeyFromField(field);
        if (!key || field.value == null || String(field.value).trim() === '') return;
        properties[key] = String(field.value).trim();
      });
      return properties;
    }

    getSimpleBundlesPrice() {
      if (!this.isSimpleBundlesMode()) return null;
      let groups = this.config?.simpleBundlesDynamicPrices;
      if (typeof groups === 'string') {
        try { groups = JSON.parse(groups); } catch (_) { groups = []; }
      }
      if (!Array.isArray(groups) || !groups.length) return null;

      const rawSelected = this.collectSimpleBundlesProperties();
      const selected = Object.fromEntries(
        Object.entries(rawSelected).map(([key, value]) => [String(key).trim(), value])
      );
      let totalMajor = 0;
      let matched = 0;

      groups.forEach((group) => {
        const name = String(group?.option_name || '').trim();
        const lookup = group?.option_lookup || {};
        const value = selected[name];
        if (!name || !value || lookup[value] == null) return;
        totalMajor += Number(lookup[value] || 0);
        matched += 1;
      });

      if (!matched || matched !== groups.length || !Number.isFinite(totalMajor)) return null;
      return Math.max(0, Math.round(totalMajor * 100));
    }

    initializeGallery() {
      if (!this.galleryViewport || !this.galleryTrack || !this.gallerySlides.length) return;

      this.galleryViewport.tabIndex = 0;

      const previous = this.querySelector('[data-gallery-previous]');
      const next = this.querySelector('[data-gallery-next]');

      this.listen(previous, 'click', () => this.goToGallery(this.galleryIndex - 1, true));
      this.listen(next, 'click', () => this.goToGallery(this.galleryIndex + 1, true));

      this.galleryThumbButtons.forEach((button) => {
        this.listen(button, 'click', () => {
          if (this.thumbWasDragged) return;
          this.goToGallery(Number(button.dataset.galleryIndex), true);
        });
        this.listen(button, 'dragstart', (event) => event.preventDefault());
      });

      this.listen(this.galleryViewport, 'scroll', () => this.handleGalleryScroll(), { passive: true });
      this.listen(this.galleryViewport, 'keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goToGallery(this.galleryIndex - 1, true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goToGallery(this.galleryIndex + 1, true);
        }
      });

      this.listen(this.gallery, 'dragstart', (event) => event.preventDefault());
      this.initializeThumbnailDrag();

      const handleMediaChange = () => this.applyGalleryPosition(false);
      if (this.galleryMedia.addEventListener) {
        this.galleryMedia.addEventListener('change', handleMediaChange, { signal: this.signal });
      } else {
        this.galleryMedia.addListener(handleMediaChange);
        this.signal.addEventListener('abort', () => this.galleryMedia.removeListener(handleMediaChange), { once: true });
      }

      this.updateGalleryState(0);
      this.applyGalleryPosition(false);
      this.preloadGalleryNeighbors(0);
    }

    initializeGalleryLightbox() {
      const template = this.querySelector('[data-gallery-lightbox-template]');
      const sourceImages = this.gallerySlides.map((slide) => slide.querySelector('img')).filter(Boolean);
      if (!template?.content?.firstElementChild || !sourceImages.length) return;

      this.galleryLightbox = template.content.firstElementChild.cloneNode(true);
      ['--cove-accent', '--cove-text', '--cove-card-bg'].forEach((property) => {
        const value = getComputedStyle(this).getPropertyValue(property);
        if (value) this.galleryLightbox.style.setProperty(property, value);
      });
      document.body.appendChild(this.galleryLightbox);

      this.galleryLightboxViewport = this.galleryLightbox.querySelector('[data-gallery-lightbox-viewport]');
      this.galleryLightboxTrack = this.galleryLightbox.querySelector('[data-gallery-lightbox-track]');
      this.galleryLightboxCurrent = this.galleryLightbox.querySelector('[data-gallery-lightbox-current]');
      this.galleryLightboxTotal = this.galleryLightbox.querySelector('[data-gallery-lightbox-total]');
      this.galleryLightboxThumbs = this.galleryLightbox.querySelector('[data-gallery-lightbox-thumbs]');

      sourceImages.forEach((source, index) => {
        const slide = document.createElement('div');
        slide.className = 'cove-gallery-lightbox__slide';
        slide.dataset.galleryLightboxSlide = '';
        slide.dataset.galleryLightboxIndex = String(index);

        const image = document.createElement('img');
        image.className = 'cove-gallery-lightbox__image';
        image.src = source.src;
        if (source.srcset) image.srcset = source.srcset;
        image.sizes = '100vw';
        image.alt = source.alt || this.config.packageName || PDP.packageImage || 'Package image';
        image.width = source.naturalWidth || source.width || 1600;
        image.height = source.naturalHeight || source.height || 1200;
        image.loading = index === this.galleryIndex ? 'eager' : 'lazy';
        image.decoding = 'async';
        image.draggable = false;
        slide.appendChild(image);
        this.galleryLightboxTrack.appendChild(slide);

        if (this.galleryLightboxThumbs) {
          const thumbButton = document.createElement('button');
          thumbButton.type = 'button';
          thumbButton.className = `cove-gallery-lightbox__thumb${index === this.galleryIndex ? ' is-active' : ''}`;
          thumbButton.dataset.galleryLightboxThumb = '';
          thumbButton.dataset.galleryLightboxIndex = String(index);
          thumbButton.setAttribute('aria-label', (PDL.showImage || 'Show image __INDEX__').replace('__INDEX__', index + 1));
          thumbButton.setAttribute('aria-pressed', index === this.galleryIndex ? 'true' : 'false');

          const thumbImage = document.createElement('img');
          thumbImage.src = source.currentSrc || source.src;
          thumbImage.alt = '';
          thumbImage.width = 96;
          thumbImage.height = 72;
          thumbImage.loading = 'lazy';
          thumbImage.decoding = 'async';
          thumbImage.draggable = false;
          thumbButton.appendChild(thumbImage);
          this.galleryLightboxThumbs.appendChild(thumbButton);
        }
      });

      this.galleryLightboxSlides = Array.from(this.galleryLightboxTrack.querySelectorAll('[data-gallery-lightbox-slide]'));
      if (this.galleryLightboxTotal) this.galleryLightboxTotal.textContent = String(this.galleryLightboxSlides.length);

      this.listen(this.galleryViewport, 'click', (event) => {
        const trigger = event.target.closest('[data-gallery-lightbox-trigger]');
        if (!trigger || Date.now() - this.galleryLastScrollTime < 180) return;
        this.openGalleryLightbox(Number(trigger.dataset.galleryIndex), trigger);
      });

      this.listen(this.galleryViewport, 'keydown', (event) => {
        const trigger = event.target.closest('[data-gallery-lightbox-trigger]');
        if (!trigger || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        this.openGalleryLightbox(Number(trigger.dataset.galleryIndex), trigger);
      });

      this.listen(this.galleryLightbox, 'click', (event) => {
        if (event.target === this.galleryLightbox || event.target.closest('[data-gallery-lightbox-close]')) {
          this.closeGalleryLightbox();
          return;
        }

        const thumb = event.target.closest('[data-gallery-lightbox-thumb]');
        if (thumb) {
          this.galleryLightboxIndex = Number(thumb.dataset.galleryLightboxIndex) || 0;
          this.renderGalleryLightbox(true);
        }
      });
      this.listen(this.galleryLightbox.querySelector('[data-gallery-lightbox-previous]'), 'click', () => this.navigateGalleryLightbox(-1));
      this.listen(this.galleryLightbox.querySelector('[data-gallery-lightbox-next]'), 'click', () => this.navigateGalleryLightbox(1));
      this.listen(this.galleryLightbox, 'keydown', (event) => this.handleGalleryLightboxKeydown(event));
      this.listen(this.galleryLightboxViewport, 'scroll', () => this.handleGalleryLightboxScroll(), { passive: true });

      const handleMediaChange = () => {
        if (!this.galleryLightbox || this.galleryLightbox.hidden) return;
        requestAnimationFrame(() => this.renderGalleryLightbox(false));
      };
      if (this.galleryMedia.addEventListener) {
        this.galleryMedia.addEventListener('change', handleMediaChange, { signal: this.signal });
      }
    }

    openGalleryLightbox(index, trigger) {
      if (!this.galleryLightbox || !this.galleryLightboxSlides.length) return;
      if (this.galleryLightboxCloseTimer) {
        clearTimeout(this.galleryLightboxCloseTimer);
        this.galleryLightboxCloseTimer = null;
      }
      this.galleryLightboxIndex = Math.max(0, Math.min(Number(index) || 0, this.galleryLightboxSlides.length - 1));
      this.galleryLightboxLastFocused = trigger || document.activeElement;
      this.lockBody();
      this.galleryLightbox.hidden = false;

      requestAnimationFrame(() => {
        this.galleryLightbox?.classList.add('is-open');
        this.renderGalleryLightbox(false);
        this.galleryLightbox?.querySelector('[data-gallery-lightbox-close]')?.focus({ preventScroll: true });
      });
    }

    closeGalleryLightbox(immediate) {
      if (!this.galleryLightbox || this.galleryLightbox.hidden) return;
      this.galleryLightbox.classList.remove('is-open');

      const finish = () => {
        if (!this.galleryLightbox) return;
        this.galleryLightbox.hidden = true;
        this.unlockBody();
        this.galleryLightboxLastFocused?.focus?.({ preventScroll: true });
        this.galleryLightboxLastFocused = null;
      };

      if (immediate) finish();
      else {
        this.galleryLightboxCloseTimer = window.setTimeout(() => {
          this.galleryLightboxCloseTimer = null;
          finish();
        }, 220);
      }
    }

    navigateGalleryLightbox(direction) {
      if (this.galleryLightboxSlides.length < 2) return;
      const count = this.galleryLightboxSlides.length;
      this.galleryLightboxIndex = (this.galleryLightboxIndex + direction + count) % count;
      this.renderGalleryLightbox(true);
    }

    renderGalleryLightbox(smooth) {
      if (!this.galleryLightboxTrack || !this.galleryLightboxViewport) return;
      this.updateGalleryLightboxState();

      if (this.galleryMedia.matches) {
        this.galleryLightboxViewport.scrollTo({
          left: this.galleryLightboxIndex * this.galleryLightboxViewport.clientWidth,
          behavior: smooth ? 'smooth' : 'auto'
        });
      } else {
        this.galleryLightboxTrack.classList.toggle('is-jumping', !smooth);
        this.galleryLightboxTrack.style.transform = `translate3d(${-this.galleryLightboxIndex * 100}%, 0, 0)`;
        if (!smooth) requestAnimationFrame(() => this.galleryLightboxTrack?.classList.remove('is-jumping'));
      }

      this.preloadGalleryLightboxNeighbors();
    }

    updateGalleryLightboxState() {
      this.galleryLightboxSlides.forEach((slide, index) => {
        const active = index === this.galleryLightboxIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      this.galleryLightboxThumbs?.querySelectorAll('[data-gallery-lightbox-thumb]').forEach((button, index) => {
        const active = index === this.galleryLightboxIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (this.galleryMedia.matches) {
        this.galleryLightboxThumbs?.querySelector('.is-active')?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
      if (this.galleryLightboxCurrent) this.galleryLightboxCurrent.textContent = String(this.galleryLightboxIndex + 1);
    }

    handleGalleryLightboxScroll() {
      if (!this.galleryMedia.matches || this.galleryLightboxScrollFrame || !this.galleryLightboxViewport) return;
      this.galleryLightboxScrollFrame = requestAnimationFrame(() => {
        this.galleryLightboxScrollFrame = null;
        const width = this.galleryLightboxViewport.clientWidth;
        if (!width) return;
        const index = Math.max(0, Math.min(Math.round(this.galleryLightboxViewport.scrollLeft / width), this.galleryLightboxSlides.length - 1));
        if (index !== this.galleryLightboxIndex) {
          this.galleryLightboxIndex = index;
          this.updateGalleryLightboxState();
          this.preloadGalleryLightboxNeighbors();
        }
      });
    }

    handleGalleryLightboxKeydown(event) {
      if (!this.galleryLightbox || this.galleryLightbox.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeGalleryLightbox();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigateGalleryLightbox(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigateGalleryLightbox(1);
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.galleryLightbox.querySelectorAll('button:not([disabled])')).filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    preloadGalleryLightboxNeighbors() {
      const count = this.galleryLightboxSlides.length;
      if (count < 2) return;
      [
        (this.galleryLightboxIndex + 1) % count,
        (this.galleryLightboxIndex - 1 + count) % count
      ].forEach((index) => {
        const image = this.galleryLightboxSlides[index]?.querySelector('img');
        if (!image) return;
        image.loading = 'eager';
        image.decode?.().catch(() => {});
      });
    }

    initializeThumbnailDrag() {
      if (!this.galleryThumbs) return;

      this.listen(this.galleryThumbs, 'pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.thumbDrag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScroll: this.galleryThumbs.scrollLeft
        };
        this.thumbWasDragged = false;
      });

      this.listen(this.galleryThumbs, 'pointermove', (event) => {
        if (!this.thumbDrag || event.pointerId !== this.thumbDrag.pointerId) return;
        const distance = event.clientX - this.thumbDrag.startX;
        if (Math.abs(distance) > 4) {
          if (!this.thumbWasDragged) this.galleryThumbs.setPointerCapture?.(event.pointerId);
          this.thumbWasDragged = true;
          this.galleryThumbs.classList.add('is-dragging');
          this.galleryThumbs.scrollLeft = this.thumbDrag.startScroll - distance;
          event.preventDefault();
        }
      });

      const finishDrag = (event) => {
        if (!this.thumbDrag || event.pointerId !== this.thumbDrag.pointerId) return;
        if (this.galleryThumbs.hasPointerCapture?.(event.pointerId)) {
          this.galleryThumbs.releasePointerCapture(event.pointerId);
        }
        this.galleryThumbs.classList.remove('is-dragging');
        this.thumbDrag = null;
        window.setTimeout(() => {
          this.thumbWasDragged = false;
        }, 0);
      };

      this.listen(this.galleryThumbs, 'pointerup', finishDrag);
      this.listen(this.galleryThumbs, 'pointercancel', finishDrag);
    }

    handleGalleryScroll() {
      if (!this.galleryMedia.matches || this.galleryScrollFrame) return;
      this.galleryLastScrollTime = Date.now();

      this.galleryScrollFrame = requestAnimationFrame(() => {
        this.galleryScrollFrame = null;
        const width = this.galleryViewport.clientWidth;
        if (!width) return;
        const index = Math.round(this.galleryViewport.scrollLeft / width);
        if (index !== this.galleryIndex) this.updateGalleryState(index);
      });
    }

    goToGallery(requestedIndex, smooth) {
      if (!this.gallerySlides.length) return;
      const count = this.gallerySlides.length;
      const index = ((requestedIndex % count) + count) % count;
      this.updateGalleryState(index);
      this.applyGalleryPosition(smooth !== false);
      this.preloadGalleryNeighbors(index);
    }

    updateGalleryState(index) {
      this.galleryIndex = Math.max(0, Math.min(index, this.gallerySlides.length - 1));

      this.gallerySlides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.galleryIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (slide.hasAttribute('data-gallery-lightbox-trigger')) {
          slide.tabIndex = active ? 0 : -1;
        }
      });

      this.galleryThumbButtons.forEach((button, buttonIndex) => {
        const active = buttonIndex === this.galleryIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      const activeThumb = this.galleryThumbButtons[this.galleryIndex];
      activeThumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    applyGalleryPosition(smooth) {
      if (!this.galleryViewport || !this.galleryTrack) return;

      if (this.galleryMedia.matches) {
        this.galleryViewport.scrollTo({
          left: this.galleryIndex * this.galleryViewport.clientWidth,
          behavior: smooth ? 'smooth' : 'auto'
        });
      } else {
        this.galleryTrack.classList.toggle('is-jumping', !smooth);
        this.galleryTrack.style.transform = `translate3d(${-this.galleryIndex * 100}%, 0, 0)`;
        if (!smooth) {
          requestAnimationFrame(() => this.galleryTrack?.classList.remove('is-jumping'));
        }
      }
    }

    preloadGalleryNeighbors(index) {
      if (this.gallerySlides.length < 2) return;
      const indexes = [
        (index + 1) % this.gallerySlides.length,
        (index - 1 + this.gallerySlides.length) % this.gallerySlides.length
      ];

      indexes.forEach((neighborIndex) => {
        const image = this.gallerySlides[neighborIndex]?.querySelector('img');
        if (!image) return;
        image.loading = 'eager';
        image.decode?.().catch(() => {});
      });
    }

    initializeCards() {
      this.cards = Array.from(this.querySelectorAll('[data-package-card]')).map((card) => {
        const data = this.readJson(card.querySelector('[data-product-json]')) || {};
        const selectedVariant = this.findVariant(data, Number(card.dataset.selectedVariantId));
        const record = {
          element: card,
          data,
          initialVariantPrice: Number(selectedVariant?.price || card.dataset.selectedPrice || 0),
          selectedVariant,
          quantity: card.dataset.packageRole === 'included'
            ? this.cardQuantitySpec(card).base
            : 0
        };

        card.__coveRecord = record;
        this.prepareSwatches(card);
        this.updateOptionAvailability(record);
        this.initChoice(record);
        if (selectedVariant) this.updateCardVariant(record, selectedVariant, false);
        this.updateAddonCardState(record);
        this.syncQuantityControl(record);
        return record;
      });

      this.listen(this, 'click', (event) => {
        const optionButton = event.target.closest('[data-option-value]');
        if (optionButton && this.contains(optionButton)) {
          this.handleOptionClick(optionButton);
          return;
        }

        const minus = event.target.closest('[data-quantity-minus]');
        if (minus && this.contains(minus)) {
          this.changeCardQuantity(minus.closest('[data-package-card]'), -1);
          return;
        }

        const plus = event.target.closest('[data-quantity-plus]');
        if (plus && this.contains(plus)) {
          this.changeCardQuantity(plus.closest('[data-package-card]'), 1);
        }
      });

      this.listen(this, 'dblclick', (event) => {
        if (!event.target.closest('[data-quantity-control]')) return;
        event.preventDefault();
      });
    }

    async initializePackageEditing() {
      const params = new URLSearchParams(window.location.search);
      const requestedInstance = String(params.get('edit_package') || '').trim();
      if (!requestedInstance) return;

      this.editLoading = true;
      this.setBusy(true);

      try {
        const cart = await this.fetchJson(this.config.routes.cart);
        const packageLines = (cart.items || []).filter((line) => {
          return String(line.properties?._package_instance || '') === requestedInstance
            && String(line.properties?._package_key || '') === String(this.config.packageKey || '');
        });

        if (!packageLines.length) {
          params.delete('edit_package');
          const query = params.toString();
          window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
          this.showToast(this.config.labels?.editUnavailable || PDP.noLongerAvailable || 'This package is no longer available in your cart.');
          return;
        }

        this.cards.forEach((record) => {
          if (record.element.dataset.packageRole !== 'addon') return;
          record.quantity = 0;
          const output = record.element.querySelector('[data-quantity-output]');
          if (output) output.textContent = '0';
          this.updateAddonCardState(record);
        });

        if (this.isSimpleBundlesMode()) {
          await this.waitForSimpleBundlesBridge();
          this.restoreSimpleBundlesPackage(packageLines);
        } else packageLines.forEach((line) => {
          const slot = String(line.properties?._package_slot || line.properties?._package_item_key || '');
          let record = this.cards.find((candidate) => {
            const candidateSlot = String(candidate.element.dataset.packageSlot || candidate.element.dataset.packageItemKey || '');
            return candidateSlot === slot;
          });

          if (!record) {
            record = this.cards.find((candidate) => {
              return (candidate.data?.variants || []).some((variant) => Number(variant.id) === Number(line.variant_id));
            });
          }
          if (!record) return;

          const variant = (record.data?.variants || []).find((candidate) => {
            return Number(candidate.id) === Number(line.variant_id);
          });
          if (variant) this.updateCardVariant(record, variant, false);

          if (record.element.dataset.packageRole === 'addon') {
            record.quantity = Math.max(0, Number(line.quantity || 0));
            const output = record.element.querySelector('[data-quantity-output]');
            if (output) output.textContent = String(record.quantity);
            this.updateAddonCardState(record);
          }
        });

        this.editPackageInstance = requestedInstance;
        this.renderEditNotice();
        this.updateTotal();
      } catch (error) {
        console.error('[Cove package] Could not load package for editing.', error);
        this.showToast(this.config.labels?.error || PDP.errorGeneric || 'Something went wrong. Please try again.');
      } finally {
        this.editLoading = false;
        this.setBusy(false);
      }
    }

    restoreSimpleBundlesPackage(lines) {
      if (!Array.isArray(lines) || !lines.length || !this.simpleBundlesReady) return;
      this.assignSimpleBundlesFields();

      const includedLine = lines.find((line) => {
        return String(line.properties?._package_role || '') === 'included_parent';
      }) || lines.find((line) => String(line.properties?._bundle_selection || '').trim()) || lines[0];

      this.cards.forEach((record) => {
        if (record.element.dataset.packageRole !== 'included') return;
        const field = record.simpleBundlesField;
        const rawKey = this.rawPropertyKeyFromField(field);
        const key = rawKey.trim();
        const storedValue = String(includedLine.properties?.[rawKey] || includedLine.properties?.[key] || '').trim();
        if (!storedValue) return;

        const normalizedStored = this.normalizeBundleValue(storedValue);
        // Infinite Options stores the option value as "<Product Title> - <Variant
        // Title>", collapsing to just "<Product Title>" when the product has a
        // single variant. Neither form appears in the bare variant strings, so
        // matching on those alone silently failed and the select fell back to the
        // first option - restoring the wrong finish or size on every edit.
        const productTitle = String(record.data?.title || '').trim();
        const singleVariant = (record.data?.variants || []).length === 1;
        const variant = (record.data?.variants || []).find((candidate) => {
          const options = Array.isArray(candidate.options) ? candidate.options.filter(Boolean) : [];
          const bare = [candidate.title, options.join(' / '), options.join(' - '), options.join(' <> '), options[0]];
          const candidates = bare.slice();
          if (productTitle) {
            bare.filter(Boolean).forEach((value) => candidates.push(`${productTitle} - ${value}`));
            // Only a one-variant product may match the bare product title; doing
            // it for a multi-variant product would pick an arbitrary variant.
            if (singleVariant) candidates.push(productTitle);
          }
          return candidates.some((value) => this.normalizeBundleValue(value) === normalizedStored);
        });

        if (variant) this.updateCardVariant(record, variant, false);
      });

      // Included extras restore the selected quantity as 1 (carried by the parent)
      // plus the extra units that were submitted as their own line.
      lines.filter((line) => String(line.properties?._package_role || '') === 'included_extra').forEach((line) => {
        const slot = String(line.properties?._package_slot || line.properties?._package_item_key || '');
        const record = this.cards.find((candidate) => {
          if (candidate.element.dataset.packageRole !== 'included') return false;
          const candidateSlot = String(candidate.element.dataset.packageSlot || candidate.element.dataset.packageItemKey || '');
          return candidateSlot === slot;
        });
        if (!record) return;

        record.quantity = this.clampFor(record, this.cardQuantitySpec(record.element).base + Number(line.quantity || 0));
        this.syncQuantityControl(record);
      });

      lines.filter((line) => String(line.properties?._package_role || '') === 'addon').forEach((line) => {
        const slot = String(line.properties?._package_slot || line.properties?._package_item_key || '');
        const record = this.cards.find((candidate) => {
          if (candidate.element.dataset.packageRole !== 'addon') return false;
          const candidateSlot = String(candidate.element.dataset.packageSlot || candidate.element.dataset.packageItemKey || '');
          return candidateSlot === slot;
        });
        if (!record) return;

        const childVariant = (record.data?.variants || []).find((variant) => {
          return Number(variant.id) === Number(line.variant_id);
        });
        if (childVariant) this.updateCardVariant(record, childVariant, false);

        record.quantity = this.clampFor(record, Number(line.quantity || 1));
        this.syncQuantityControl(record);
        this.updateAddonCardState(record);
      });

      this.syncAllSimpleBundlesSelections(true);
      this.updateTotal();
    }

    renderEditNotice() {
      if (!this.editPackageInstance) return;

      if (!this.editNotice) {
        const notice = document.createElement('div');
        notice.className = 'cove-package__edit-notice';
        Object.assign(notice.style, {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '14px',
          margin: '0 auto 18px',
          padding: '13px 16px',
          maxWidth: '1900px',
          color: 'var(--cove-text, #222)',
          background: 'var(--cove-card-bg, #e7eceb)',
          border: '1px solid rgba(34, 34, 34, .14)',
          borderRadius: '10px'
        });

        const text = document.createElement('p');
        text.style.margin = '0';
        // 13px is this component's established running-text size - seven other
        // rules in package-details.css use it, and --cove-feature-font-size
        // defaults to it. DESIGN.md's ramp is the brand scale, not an
        // exhaustive UI scale, and nothing in it forbids this step.
        text.style.fontSize = '13px';
        text.style.lineHeight = '1.4';
        text.textContent = (PDP.editingLoaded || 'Editing __NAME__ — your saved selections are loaded.').replace('__NAME__', this.config.packageName || 'package');

        const cancel = document.createElement('a');
        cancel.href = `${window.Shopify?.routes?.root || '/'}cart`;
        cancel.textContent = PDP.cancelEdit || 'CANCEL EDIT';
        // DESIGN.md's Wide-Label Rule: micro-labels are 11px at 0.22em and are
        // "never bolded to compensate for size, and never set below 11px".
        // This one was 10px, 700 and .06em - off the ramp on all three counts.
        // Now the documented label token, with a taller hit area so an 11px
        // link is still comfortably tappable.
        //
        // Tracking is Latin-only. pv-rtl.css resets letter-spacing for Arabic
        // because it breaks the joins between letters, and an inline style
        // would outrank that stylesheet, so the decision is made here instead.
        const isRtl = (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
        Object.assign(cancel.style, {
          flex: '0 0 auto',
          display: 'inline-flex',
          alignItems: 'center',
          minHeight: '24px',
          color: 'inherit',
          fontSize: '11px',
          fontWeight: '500',
          lineHeight: '1.2',
          letterSpacing: isRtl ? 'normal' : '0.22em',
          textDecoration: 'underline'
        });

        notice.append(text, cancel);
        this.querySelector('.cove-package__inner')?.prepend(notice);
        this.editNotice = notice;
      }
    }

    findVariant(productData, variantId) {
      if (!Array.isArray(productData?.variants)) return null;
      return productData.variants.find((variant) => Number(variant.id) === Number(variantId)) || productData.variants.find((variant) => variant.available) || productData.variants[0] || null;
    }

    getDiscountPercent() {
      if ((this.config?.discountMethod || 'none') === 'none') return 0;
      return Math.max(0, Math.min(100, Number(this.config?.discountPercent || 0)));
    }

    getDiscountedPrice(regularPrice) {
      return Math.max(0, Math.round(Number(regularPrice || 0) * (100 - this.getDiscountPercent()) / 100));
    }

    updateCardPrice(record, regularPrice) {
      const card = record.element;
      // Included extras and add-ons are both discounted server-side by the automatic
      // Shopify Function, so every card shows the same discounted unit price.
      const displayedPrice = this.getDiscountPercent() > 0
        ? this.getDiscountedPrice(regularPrice)
        : Number(regularPrice || 0);
      const hasDiscount = displayedPrice < Number(regularPrice || 0);
      const originalPrice = card.querySelector('[data-card-original-price]');
      const currentPrice = card.querySelector('[data-card-price]');

      if (originalPrice) {
        originalPrice.innerHTML = this.formatMoney(regularPrice);
        originalPrice.hidden = !hasDiscount;
      }
      if (currentPrice) {
        currentPrice.innerHTML = this.formatMoney(displayedPrice);
      }
      card.classList.toggle('has-package-discount', hasDiscount);
    }

    prepareSwatches(card) {
      const fallbackColors = {
        beige: '#dfcfaa',
        amber: '#bd7544',
        white: '#f7f7f4',
        black: '#161616',
        grey: '#9d9d9b',
        gray: '#9d9d9b',
        cream: '#eee4ca',
        ivory: '#f0ead6',
        brown: '#815c43',
        green: '#68745a',
        blue: '#597289',
        orange: '#c6753d',
        red: '#a34e45',
        natural: '#c9b998',
        oak: '#b69368'
      };
      const themeMap = window.themeSettings?.customColorMap || {};

      card.querySelectorAll('[data-is-color="true"]').forEach((button) => {
        const swatch = button.querySelector('.cove-package-card__swatch');
        if (!swatch || getComputedStyle(swatch).backgroundImage !== 'none') return;

        // The taxonomy swatch wins, and it wins before anything is guessed.
        //
        // Every colour option on this catalogue is linked to the
        // `shopify.color-pattern` taxonomy, so Liquid already printed the
        // merchant's own hex on the button as `--swatch-color`. That value is
        // the SAME in every language, because it belongs to the option value
        // rather than to its name.
        //
        // The map below, by contrast, is keyed on the option's VISIBLE TEXT.
        // On /ar Shopify hands us بيج and عنبري, which match no key and are not
        // CSS colours, so every colour in the Arabic package builder fell
        // through to the neutral `#d3d0c8` and two different colours rendered
        // identically. It also overwrote the real hex on the English page with
        // an approximation (#dfcfaa for #EAD8AB), so the builder disagreed with
        // the product page and the collection cards about the same colour.
        //
        // So: if Liquid supplied a swatch colour, leave it alone. The map stays
        // as the fallback for an option that has no taxonomy swatch at all.
        if (button.style.getPropertyValue('--swatch-color').trim()) return;

        const value = button.dataset.optionValue || '';
        const normalized = value.trim().toLowerCase();
        const mapped = themeMap[value] || themeMap[normalized] || fallbackColors[normalized];
        if (mapped) {
          swatch.style.backgroundColor = mapped;
        } else if (window.CSS?.supports?.('color', value)) {
          swatch.style.backgroundColor = value;
        } else {
          swatch.style.backgroundColor = '#d3d0c8';
        }
      });
    }

    // D1 (Anas 2026-10-02): every piece inside a package opens with its
    // options NOT chosen (colour, side). The package still prices on the
    // opening variant (all colours and sides of a piece cost the same), but
    // Buy now and Add to cart wait until the customer has chosen every open
    // group. A group with a single selectable value (for example the Dining
    // Entertainer table locked to 260 x 110) counts as chosen. Editing a
    // package already in the cart restores the customer's own choices.
    initChoice(record) {
      const fieldsets = Array.from(record.element.querySelectorAll('[data-option-index]'));
      const editing = Boolean(new URLSearchParams(window.location.search).get('edit_package'));
      record.chosen = new Set();
      fieldsets.forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        const selectable = Array.from(fieldset.querySelectorAll('[data-option-value]')).filter((button) => !button.disabled);
        if (editing || selectable.length <= 1) record.chosen.add(index);
      });
    }

    choiceOpenGroups(record) {
      if (!record?.chosen) return [];
      return Array.from(record.element.querySelectorAll('[data-option-index]')).filter((fieldset) => {
        return !record.chosen.has(Number(fieldset.dataset.optionIndex));
      });
    }

    renderChoice(record, show) {
      if (!record?.chosen) return;
      record.element.querySelectorAll('[data-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        let prompt = fieldset.querySelector('[data-package-choice]');
        if (record.chosen.has(index)) {
          fieldset.classList.remove('is-choice-missing');
          if (prompt) prompt.hidden = true;
          return;
        }
        fieldset.querySelectorAll('[data-option-value]').forEach((button) => {
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
        });
        if (!show) return;
        if (!prompt) {
          prompt = document.createElement('p');
          prompt.className = 'cove-package-card__choice';
          prompt.setAttribute('data-package-choice', '');
          prompt.setAttribute('role', 'status');
          prompt.setAttribute('aria-live', 'polite');
          const legend = fieldset.querySelector('legend');
          if (legend) legend.insertAdjacentElement('afterend', prompt);
          else fieldset.prepend(prompt);
        }
        prompt.textContent = this.choiceText(fieldset.querySelector('legend')?.textContent);
        prompt.hidden = false;
        fieldset.classList.remove('is-choice-missing');
        void fieldset.offsetWidth;
        fieldset.classList.add('is-choice-missing');
      });
    }

    choiceText(groupName) {
      const ar = (document.documentElement.lang || 'en').toLowerCase().indexOf('ar') === 0;
      const strings = (window.PovelleaI18n && window.PovelleaI18n.choice) || {};
      const name = String(groupName || '').trim();
      const key = name.toLowerCase();
      if (/colou?r|اللون|لون/.test(key)) return strings.colour || (ar ? 'اختر اللون' : 'Choose a colour');
      if (/side|orientation|الجهة|الاتجاه|جهة/.test(key)) return strings.side || (ar ? 'اختر الجهة' : 'Choose a side');
      if (/size|المقاس|الحجم|مقاس/.test(key)) return strings.size || (ar ? 'اختر المقاس' : 'Choose a size');
      return (strings.option || (ar ? 'اختر __NAME__' : 'Choose __NAME__')).replace('__NAME__', ar ? name : name.toLowerCase());
    }

    // Returns true when every needed piece has every option chosen; otherwise
    // shows the prompts, a short note beside the buttons, and brings the first
    // open piece into view.
    ensureChoicesComplete() {
      const open = this.cards.filter((record) => {
        const needed = record.element.dataset.packageRole === 'included' || Number(record.quantity) > 0;
        return needed && this.choiceOpenGroups(record).length > 0;
      });
      this.querySelectorAll('[data-package-choice-note]').forEach((note) => { note.hidden = open.length === 0; });
      if (!open.length) return true;

      open.forEach((record) => this.renderChoice(record, true));
      const ar = (document.documentElement.lang || 'en').toLowerCase().indexOf('ar') === 0;
      const strings = (window.PovelleaI18n && window.PovelleaI18n.choice) || {};
      const noteText = strings.packagePieces || (ar ? 'اختر الخيارات لكل قطعة في الأسفل.' : 'Choose the options for each piece below.');
      this.querySelectorAll('[data-package-action="buy"]').forEach((button) => {
        const holder = button.closest('.cove-package__desktop-actions, .cove-package__mobile-actions');
        if (!holder || holder.querySelector('[data-package-choice-note]')) return;
        const note = document.createElement('p');
        note.className = 'cove-package__choice-note';
        note.setAttribute('data-package-choice-note', '');
        note.setAttribute('role', 'status');
        note.setAttribute('aria-live', 'polite');
        holder.appendChild(note);
      });
      // Conversion fixes 2026-10-03: in the phone sticky bar the piece options are
      // ABOVE the bar, beside the price they are below.
      const noteAbove = strings.packagePiecesAbove || (ar ? 'اختر الخيارات لكل قطعة في الأعلى.' : 'Choose the options for each piece above.');
      this.querySelectorAll('[data-package-choice-note]').forEach((note) => {
        note.textContent = note.closest('.cove-package__mobile-actions') ? noteAbove : noteText;
        note.hidden = false;
      });

      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const firstGroup = this.choiceOpenGroups(open[0])[0] || open[0].element;
      firstGroup.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      return false;
    }

    handleOptionClick(button) {
      if (button.disabled) return;
      const card = button.closest('[data-package-card]');
      const record = card?.__coveRecord;
      const fieldset = button.closest('[data-option-index]');
      if (!record || !fieldset) return;

      const optionIndex = Number(fieldset.dataset.optionIndex);
      // D1: start from the real (priced) variant, not from what is highlighted,
      // because open groups show nothing highlighted.
      const selectedOptions = Array.isArray(record.selectedVariant?.options)
        ? record.selectedVariant.options.slice()
        : this.getSelectedOptions(card);
      selectedOptions[optionIndex] = button.dataset.optionValue;

      let variant = record.data.variants.find((candidate) => this.optionsEqual(candidate.options, selectedOptions));

      if (!variant?.available) {
        variant = record.data.variants.find((candidate) => candidate.available && candidate.options?.[optionIndex] === button.dataset.optionValue) || variant;
      }

      // Price lock (see the section): a fixed-price bundle must never move to a
      // variant the checkout will not charge for. Prefer a same-price variant
      // carrying this option value; otherwise ignore the click.
      const lockedPrice = Number(card.dataset.lockedPrice || 0);
      if (lockedPrice > 0 && variant && Number(variant.price) !== lockedPrice) {
        variant = record.data.variants.find((candidate) => candidate.available
          && Number(candidate.price) === lockedPrice
          && candidate.options?.[optionIndex] === button.dataset.optionValue) || null;
        if (!variant) return;
      }

      if (variant) {
        record.chosen?.add(optionIndex);
        this.updateCardVariant(record, variant, true);
        if (this.querySelector('[data-package-choice-note]:not([hidden])')) {
          const stillOpen = this.cards.some((item) => {
            const needed = item.element.dataset.packageRole === 'included' || Number(item.quantity) > 0;
            return needed && this.choiceOpenGroups(item).length > 0;
          });
          if (!stillOpen) this.querySelectorAll('[data-package-choice-note]').forEach((note) => { note.hidden = true; });
        }
      }
    }

    getSelectedOptions(card) {
      return Array.from(card.querySelectorAll('[data-option-index]')).map((fieldset) => {
        return fieldset.querySelector('[data-option-value].is-active')?.dataset.optionValue || '';
      });
    }

    optionsEqual(left, right) {
      if (!Array.isArray(left) || left.length !== right.length) return false;
      return left.every((value, index) => value === right[index]);
    }

    updateCardVariant(record, variant, updateTotals) {
      const card = record.element;
      record.selectedVariant = variant;
      card.dataset.selectedVariantId = String(variant.id);
      card.dataset.selectedPrice = String(variant.price || 0);
      card.dataset.variantAvailable = variant.available ? 'true' : 'false';

      Array.from(card.querySelectorAll('[data-option-index]')).forEach((fieldset) => {
        const optionIndex = Number(fieldset.dataset.optionIndex);
        const value = variant.options?.[optionIndex];
        fieldset.querySelectorAll('[data-option-value]').forEach((button) => {
          const active = button.dataset.optionValue === value;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      });

      this.updateCardPrice(record, Number(variant.price || 0));

      const availability = card.querySelector('[data-card-availability]');
      if (availability) {
        availability.hidden = Boolean(variant.available);
        availability.textContent = variant.available ? '' : (this.config.labels?.unavailable || PDP.unavailable || 'Unavailable');
      }

      // `variant_images` is resolved from ALT text the way the product page
      // resolves its gallery, and wins over `featured_image`: a variant can
      // only be assigned one photograph, and on this catalogue every
      // right-facing variant is assigned the left-facing one, so the builder
      // showed a left-facing sofa to someone who had chosen right.
      const altImage = record.data?.variant_images?.[String(variant.id)];
      const featuredImage = variant.featured_image?.src || variant.featured_image?.url;
      const cardImage = card.querySelector('.cove-package-card__image');
      if (cardImage && (altImage?.card || featuredImage)) {
        cardImage.removeAttribute('srcset');
        cardImage.removeAttribute('sizes');
        cardImage.src = altImage?.card || this.shopifyImageUrl(featuredImage, 720);
        if (altImage?.alt) cardImage.alt = altImage.alt;
      }

      this.updateOptionAvailability(record);
      this.renderChoice(record);
      if (updateTotals !== false) {
        this.syncSimpleBundlesSelection(record, true);
        this.updateBundleSelectionInput();
        this.updateTotal();
      }
    }

    updateOptionAvailability(record) {
      const lockedPrice = Number(record.element.dataset.lockedPrice || 0);
      // Fix batch 2026-10-03: a variant locked by the package (Dining
      // Entertainer: the 260 x 110 table only) is the only one that can be chosen.
      const lockedVariantId = String(record.element.dataset.lockedVariantId || '');
      const availableVariants = (record.data.variants || []).filter((variant) => variant.available
        && (lockedPrice <= 0 || Number(variant.price) === lockedPrice)
        && (!lockedVariantId || String(variant.id) === lockedVariantId));
      record.element.querySelectorAll('[data-option-index]').forEach((fieldset) => {
        const optionIndex = Number(fieldset.dataset.optionIndex);
        fieldset.querySelectorAll('[data-option-value]').forEach((button) => {
          const hasAvailableVariant = availableVariants.some((variant) => variant.options?.[optionIndex] === button.dataset.optionValue);
          button.disabled = !hasAvailableVariant;
          button.setAttribute('aria-disabled', hasAvailableVariant ? 'false' : 'true');
        });
      });
    }

    // Per-card quantity contract. An included card reads its real base quantity
    // from the markup; min is ALWAYS the base, because a customer may add to a
    // package but may never take delivery of fewer pieces than it contains.
    cardQuantitySpec(card) {
      if (card?.dataset?.packageRole === 'addon') {
        const maximum = this.isSimpleBundlesMode()
          ? Math.max(1, Number(this.config?.addonMaxQuantity || 5))
          : 99;
        return { base: 0, min: 0, max: maximum, step: 1 };
      }
      const rawBase = Math.floor(Number(card?.dataset?.baseQuantity));
      const base = Number.isFinite(rawBase) && rawBase >= 1 ? rawBase : INCLUDED_MIN_QUANTITY;
      const rawMax = Math.floor(Number(card?.dataset?.maxQuantity));
      const max = Number.isFinite(rawMax) && rawMax >= base
        ? rawMax
        : Math.max(base, INCLUDED_MAX_QUANTITY);
      const rawStep = Math.floor(Number(card?.dataset?.stepQuantity));
      const step = Number.isFinite(rawStep) && rawStep >= 1 ? rawStep : 1;
      return { base, min: base, max, step };
    }

    quantitySpecFor(target) {
      return this.cardQuantitySpec(target?.element || target);
    }

    quantityBoundsFor(target) {
      const { min, max } = this.quantitySpecFor(target);
      return { min, max };
    }

    // Single clamping authority. Every button, programmatic and pre-serialisation
    // path routes through this, so a card can never leave its own base..max range.
    clampFor(target, value) {
      const { min, max } = this.quantitySpecFor(target);
      const parsed = Math.floor(Number(value));
      if (!Number.isFinite(parsed)) return min;
      return Math.max(min, Math.min(max, parsed));
    }

    changeCardQuantity(card, change) {
      const record = card?.__coveRecord;
      if (!record) return;
      const role = card.dataset.packageRole;
      if (role !== 'addon' && role !== 'included') return;

      record.quantity = this.clampFor(record, Number(record.quantity || 0) + change);
      this.syncQuantityControl(record);

      if (role === 'addon') {
        this.updateAddonCardState(record);
        this.syncSimpleBundlesSelection(record, true);
      }
      this.updateBundleSelectionInput();
      this.updateTotal();
    }

    changeAddonQuantity(card, change) {
      this.changeCardQuantity(card, change);
    }

    // Re-clamps the stored quantity and mirrors it into the <output> and the
    // button disabled states. Safe to call after any programmatic change.
    syncQuantityControl(record) {
      if (!record) return;
      const card = record.element;
      const role = card.dataset.packageRole;
      if (role !== 'addon' && role !== 'included') return;

      record.quantity = this.clampFor(record, record.quantity);
      const { min, max } = this.quantityBoundsFor(record);

      const output = card.querySelector('[data-quantity-output]');
      if (output) output.textContent = String(record.quantity);

      const minus = card.querySelector('[data-quantity-minus]');
      if (minus) minus.disabled = record.quantity <= min;
      const plus = card.querySelector('[data-quantity-plus]');
      if (plus) plus.disabled = record.quantity >= max;
    }

    updateAddonCardState(record) {
      if (!record || record.element.dataset.packageRole !== 'addon') return;

      const quantity = Math.max(0, Number(record.quantity || 0));
      const selected = quantity > 0;
      const status = record.element.querySelector('[data-addon-status]');
      const count = record.element.querySelector('[data-addon-count]');

      record.element.classList.toggle('is-added-to-package', selected);

      if (status) {
        status.hidden = !selected;
        status.textContent = this.config.labels?.addonAdded || PDP.addedToPackage || 'Added to package';
      }

      if (count) {
        count.hidden = !selected;
        count.textContent = String(quantity);
        count.setAttribute('aria-label', `${quantity} added to package`);
      }
    }

    // Mirrors exactly the extra lines that will be POSTed to /cart/add.js so the
    // estimate rounds at the same granularity Shopify does (per line, integer cents).
    packageExtraLineEstimates() {
      const discountPercent = this.getDiscountPercent();
      const lines = [];

      this.cards.forEach((record) => {
        const role = record.element.dataset.packageRole;
        const price = Number(record.selectedVariant?.price || 0);
        if (!price) return;

        if (role === 'included') {
          const extra = this.clampFor(record, record.quantity) - this.cardQuantitySpec(record.element).base;
          if (extra > 0) lines.push({ role: 'included_extra', regular: price * extra });
          return;
        }

        if (role !== 'addon') return;
        const quantity = this.clampFor(record, record.quantity);
        if (quantity > 0) lines.push({ role: 'addon', regular: price * quantity });
      });

      return lines.map((line) => Object.assign({}, line, {
        discounted: Math.max(0, Math.round(line.regular * (100 - discountPercent) / 100))
      }));
    }

    updateTotal() {
      let packageSubtotal = 0;
      let includedSubtotal = 0;

      this.cards.forEach((record) => {
        const role = record.element.dataset.packageRole;
        const price = Number(record.selectedVariant?.price || 0);
        if (role === 'included') {
          const quantity = this.clampFor(record, record.quantity);
          packageSubtotal += price * quantity;
          // The Simple Bundles parent carries the package BASE quantity of every
          // included product - which is six chairs in a six-seat dining package,
          // not one. Anything above the base is submitted as its own line.
          includedSubtotal += price * this.cardQuantitySpec(record.element).base;
        } else if (Number(record.quantity) > 0) {
          packageSubtotal += price * this.clampFor(record, record.quantity);
        }
      });

      const discountPercent = this.getDiscountPercent();
      const extraLines = this.packageExtraLineEstimates();
      const extrasTotal = extraLines.reduce((sum, line) => sum + line.discounted, 0);
      const simpleBundlesTotal = this.getSimpleBundlesPrice();
      const calculatedTotal = Math.max(0, Math.round(packageSubtotal * (100 - discountPercent) / 100));
      const simpleBundlesFallback = Math.max(0, Math.round(includedSubtotal * (100 - discountPercent) / 100));
      // getSimpleBundlesPrice() returns the RAW sum of the selected option
      // prices the app publishes, with no package discount in it - the fixed
      // price it replaces had the discount baked in, this does not. The tier
      // percentage is applied here, exactly as the Liquid does when it renders
      // the server-side total, so the page never shows a number the cart will
      // not charge. Extras are added afterwards already discounted by
      // packageExtraLineEstimates(), so nothing is discounted twice.
      const simpleBundlesDynamic = simpleBundlesTotal == null
        ? null
        : Math.max(0, Math.round(simpleBundlesTotal * (100 - discountPercent) / 100));
      const simpleBundlesBase = simpleBundlesDynamic == null ? simpleBundlesFallback : simpleBundlesDynamic;
      const total = this.isSimpleBundlesMode()
        ? simpleBundlesBase + extrasTotal
        : calculatedTotal;
      const hasDiscount = total < packageSubtotal;
      this.currentSubtotal = packageSubtotal;
      this.currentTotal = total;

      this.originalPriceOutputs.forEach((output) => {
        output.innerHTML = this.formatMoney(packageSubtotal);
        output.hidden = !hasDiscount;
      });

      this.priceOutputs.forEach((output) => {
        output.innerHTML = this.formatMoney(total);
      });

      this.classList.toggle('cove-package--has-discount', hasDiscount);
    }

    initializeAccordions() {
      this.listen(this, 'click', (event) => {
        const button = event.target.closest('[data-accordion-button]');
        if (!button || !this.contains(button)) return;

        const item = button.closest('[data-accordion]');
        const panel = item?.querySelector('[data-accordion-panel]');
        if (!item || !panel) return;

        const shouldOpen = button.getAttribute('aria-expanded') !== 'true';

        this.querySelectorAll('[data-accordion]').forEach((accordion) => {
          const accordionButton = accordion.querySelector('[data-accordion-button]');
          const accordionPanel = accordion.querySelector('[data-accordion-panel]');
          accordionButton?.setAttribute('aria-expanded', 'false');
          accordion.classList.remove('is-open');
          if (accordionPanel) accordionPanel.hidden = true;
        });

        if (shouldOpen) {
          button.setAttribute('aria-expanded', 'true');
          item.classList.add('is-open');
          panel.hidden = false;
        }
      });
    }

    initializeActions() {
      this.listen(this, 'click', (event) => {
        const action = event.target.closest('[data-package-action]');
        if (!action || !this.contains(action)) return;
        this.handlePackageAction(action.dataset.packageAction);
      });
    }

    initializeMobileBarVisibility() {
      if (!this.mobileBar || !this.mobilePurchaseEnd) return;

      const update = () => {
        this.mobileBarFrame = null;

        const header = document.querySelector(
          'povellea-header, .pv-header-shell, .pv-header, sticky-header, .header-wrapper, header.header'
        );
        const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const purchaseEnd = this.mobilePurchaseEnd.getBoundingClientRect();
        const shouldShow = this.galleryMedia.matches
          && !this.classList.contains('cove-package--comparison')
          && purchaseEnd.bottom <= headerBottom;

        this.mobileBar.classList.toggle('is-visible', shouldShow);
        this.mobileBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        this.mobileBar.inert = !shouldShow;
      };

      const schedule = () => {
        if (this.mobileBarFrame) return;
        this.mobileBarFrame = requestAnimationFrame(update);
      };

      this.mobileBar.setAttribute('aria-hidden', 'true');
      this.mobileBar.inert = true;

      if ('IntersectionObserver' in window) {
        this.mobileBarObserver = new IntersectionObserver(schedule, { threshold: [0, 1] });
        this.mobileBarObserver.observe(this.mobilePurchaseEnd);
      }

      this.listen(window, 'scroll', schedule, { passive: true });
      this.listen(window, 'resize', schedule, { passive: true });

      if (this.galleryMedia.addEventListener) {
        this.galleryMedia.addEventListener('change', schedule, { signal: this.signal });
      } else {
        this.galleryMedia.addListener(schedule);
        this.signal.addEventListener('abort', () => this.galleryMedia.removeListener(schedule), { once: true });
      }

      schedule();
    }

    async handlePackageAction(action) {
      if (this.busy) return;
      if (!this.ensureChoicesComplete()) return;

      if (this.isSimpleBundlesMode()) {
        const ready = await this.waitForSimpleBundlesBridge();
        const selectionStatus = ready ? this.syncAllSimpleBundlesSelections(true) : { valid: false, invalid: this.cards };
        if (!selectionStatus.valid) {
          const firstInvalid = selectionStatus.invalid[0];
          firstInvalid?.element?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          this.showToast('One or more package options are not mapped in Simple Bundles. Check the app option names and values.');
          return;
        }

      }

      if (!this.isSimpleBundlesMode() && this.config.discountMethod === 'code' && Number(this.config.discountPercent || 0) > 0 && !String(this.config.discountCode || '').trim()) {
        this.showToast(this.config.labels?.missingDiscountCode || PDP.missingDiscountCode || 'Add the matching Shopify discount code in the section settings.');
        return;
      }

      const invalid = this.cards.find((record) => {
        const needed = record.element.dataset.packageRole === 'included' || record.quantity > 0;
        return needed && !record.selectedVariant?.available;
      });

      if (invalid) {
        invalid.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        invalid.element.querySelector('[data-card-availability]')?.focus?.();
        this.showToast(this.config.labels?.unavailable || PDP.unavailable || 'Unavailable');
        return;
      }

      this.setBusy(true, action);
      const wasEditing = Boolean(this.editPackageInstance);

      try {
        const cart = await this.syncPackageCart();
        this.notifyCartUpdated(cart);

        if (action === 'buy') {
          window.location.assign(this.config.routes?.checkout || '/checkout');
          return;
        }

        // Conversion fixes 2026-10-03 (Anas): a package added to the cart opens the
        // cart drawer, like a single piece, so the buyer sees it and the delivery and
        // cancellation lines. The toast stays as the fallback (no drawer, or an edit).
        const openedDrawer = !wasEditing && await this.openCartDrawer(cart);
        if (!openedDrawer) {
          this.showToast(
            wasEditing
              ? (this.config.labels?.updated || PDP.packageUpdated || 'Package updated')
              : (this.config.labels?.added || PDP.addedToCart || 'Added to cart')
          );
        }
      } catch (error) {
        console.error('[Cove package] Cart update failed.', error);
        const message = error.code === 'PACKAGE_DISCOUNT_INVALID'
          ? (this.config.labels?.invalidDiscount || PDP.errorDiscount || 'The configured Shopify discount is not applicable to this package.')
          : (this.config.labels?.error || PDP.errorGeneric || 'Something went wrong. Please try again.');
        this.showToast(message);
      } finally {
        this.setBusy(false);
      }
    }

    createPackageInstance() {
      const packageKey = String(this.config.packageKey || 'package').trim() || 'package';
      let uniqueId = '';

      if (window.crypto?.randomUUID) {
        uniqueId = window.crypto.randomUUID();
      } else if (window.crypto?.getRandomValues) {
        const randomValues = new Uint32Array(4);
        window.crypto.getRandomValues(randomValues);
        uniqueId = Array.from(randomValues, (value) => value.toString(36)).join('-');
      } else {
        uniqueId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      }

      return `${packageKey}:${uniqueId}`;
    }

    getPackagePageUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete('edit_package');
      url.hash = '';
      return `${url.pathname}${url.search}`;
    }

    buildDesiredPackageItems(packageInstance) {
      const discountCode = this.config.discountMethod === 'code' ? String(this.config.discountCode || '').trim() : '';
      const packageUrl = this.getPackagePageUrl();

      return this.cards.reduce((items, record) => {
        const role = record.element.dataset.packageRole;
        const quantity = role === 'included' ? 1 : record.quantity;
        if (!quantity || !record.selectedVariant?.id) return items;
        const packageSlot = record.element.dataset.packageSlot || record.element.dataset.packageItemKey;

        items.push({
          id: Number(record.selectedVariant.id),
          quantity,
          properties: {
            _package_key: this.config.packageKey,
            _package_instance: packageInstance,
            _package_slot: packageSlot,
            _package_item_key: record.element.dataset.packageItemKey,
            _package_role: role,
            _package_name: this.config.packageName || 'Package',
            _package_url: packageUrl,
            _package_discount_percent: String(this.config.discountMethod === 'none' ? 0 : Number(this.config.discountPercent || 0)),
            _package_discount_code: discountCode
          }
        });
        return items;
      }, []);
    }

    buildSimpleBundlesParentItems(packageInstance) {
      const packageUrl = this.getPackagePageUrl();
      // Physical pieces, not product cards. A six-chair dining package is seven
      // pieces, not two.
      const includedPieceCount = this.cards.reduce((sum, record) => {
        if (record.element.dataset.packageRole !== 'included') return sum;
        return sum + this.clampFor(record, record.quantity);
      }, 0);
      const commonProperties = {
        _package_key: this.config.packageKey,
        _package_instance: packageInstance,
        _package_name: this.config.packageName || 'Package',
        _package_url: packageUrl,
        _package_type: 'simple_bundles',
        _package_discount_percent: String(this.getDiscountPercent())
      };
      const includedProperties = this.collectSimpleBundlesProperties();

      Object.assign(includedProperties, commonProperties, {
        _package_role: 'included_parent',
        _package_role_tier: this.tieredRole('included_parent'),
        _package_tier: String(this.packageTier()),
        _package_item_count: String(includedPieceCount)
      });

      // The Simple Bundles properties above are the app's own record of the
      // configuration and are always in the shop's primary language, so the
      // cart cannot read a component's chosen options out of them and show
      // them to an Arabic customer. Record the variant each component was
      // configured to as a private property keyed by the component's handle:
      // an id carries the choice without carrying a language, and the cart
      // resolves it back to a variant whose title Shopify returns translated.
      // Private (underscore) keys are hidden from the customer and ignored by
      // the app, so nothing the commerce path depends on changes.
      const handleize = (value) => String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      this.cards.forEach((record) => {
        if (record.element.dataset.packageRole !== 'included') return;
        const variantId = Number(record.selectedVariant?.id || 0);
        if (!variantId) return;
        // The cart looks this up under the handleized Simple Bundles property
        // key, which is usually the product handle but is not guaranteed to be:
        // "Cove Single Sofa" is the key for the product with the handle
        // cove-armchair. Write both spellings so the lookup cannot miss.
        const keys = new Set([
          handleize(record.data?.handle),
          handleize(this.propertyKeyFromField(record.simpleBundlesField))
        ].filter(Boolean));
        keys.forEach((key) => {
          includedProperties[`_pv_v_${key}`] = String(variantId);
        });
      });

      const items = [{
        id: Number(this.config.parentVariantId),
        quantity: 1,
        properties: includedProperties
      }];

      this.cards.forEach((record) => {
        const role = record.element.dataset.packageRole;
        const selectedVariant = record.selectedVariant;
        if (!selectedVariant?.id) return;
        const packageSlot = record.element.dataset.packageSlot || record.element.dataset.packageItemKey;

        // Included products: the parent already carries one unit, so only the
        // additional N-1 units are submitted as their own discountable line.
        if (role === 'included') {
          const extra = this.clampFor(record, record.quantity) - this.cardQuantitySpec(record.element).base;
          if (extra <= 0) return;
          items.push({
            id: Number(selectedVariant.id),
            quantity: extra,
            properties: Object.assign({}, commonProperties, {
              _package_role: 'included_extra',
              _package_role_tier: this.tieredRole('included_extra'),
              _package_tier: String(this.packageTier()),
              _package_slot: packageSlot,
              _package_item_key: record.element.dataset.packageItemKey,
              _package_item_count: String(extra)
            })
          });
          return;
        }

        if (role !== 'addon') return;
        const quantity = this.clampFor(record, record.quantity);
        if (quantity <= 0) return;

        items.push({
          id: Number(selectedVariant.id),
          quantity,
          properties: Object.assign({}, commonProperties, {
            _package_role: 'addon',
            _package_role_tier: this.tieredRole('addon'),
            _package_tier: String(this.packageTier()),
            _package_slot: packageSlot,
            _package_item_key: record.element.dataset.packageItemKey,
            _package_item_count: String(quantity)
          })
        });
      });

      return items;
    }

    // Malformed per-item quantity data must never reach the cart: a wrong base
    // quantity silently mis-prices the package and ships the wrong piece count.
    // The section sets quantityDataValid=false when item_quantities does not
    // match included_products exactly, and we refuse rather than guess.
    // Discount TIER for this package, as an integer percentage (5 or 10). It comes
    // from the package product's own discount_percentage metafield, rendered into
    // the section - never from anything the browser could invent.
    packageTier() {
      const pct = Math.round(Number(this.config?.discountPercent || 0));
      return Number.isFinite(pct) && pct > 0 ? pct : 0;
    }

    // Tier-encoded role, e.g. included_extra_5 / addon_10. Emitted ALONGSIDE the
    // legacy _package_role, never instead of it: the currently deployed Function
    // matches ^(included_extra|addon)$, and removing that value would leave the
    // live reference package's extras undiscounted. The replacement Functions
    // gate on _package_role_tier plus a matching-tier parent.
    tieredRole(role) {
      const tier = this.packageTier();
      return tier ? role + '_' + tier : role;
    }

    quantityDataIsUsable() {
      return this.config?.quantityDataValid !== false;
    }

    async syncPackageCart() {
      if (!this.quantityDataIsUsable()) {
        throw new Error('This package is being updated and cannot be added to the cart right now.');
      }
      const previousPackageInstance = String(this.editPackageInstance || '');
      const packageInstance = this.createPackageInstance();
      const desired = this.isSimpleBundlesMode()
        ? this.buildSimpleBundlesParentItems(packageInstance)
        : this.buildDesiredPackageItems(packageInstance);
      if (!desired.length) throw new Error(PDP.noAvailableItems || 'The package has no available items.');

      await this.fetchJson(this.config.routes.add, {
        method: 'POST',
        body: JSON.stringify({ items: desired })
      });

      let cart = await this.fetchJson(this.config.routes.cart);

      const discountCode = !this.isSimpleBundlesMode() && this.config.discountMethod === 'code'
        ? String(this.config.discountCode || '').trim()
        : '';
      if (discountCode) {
        const existingCodes = Array.isArray(cart.discount_codes)
          ? cart.discount_codes.map((entry) => String(entry.code || '').trim()).filter(Boolean)
          : [];
        const combinedCodes = Array.from(new Set(existingCodes.concat(discountCode))).join(',');
        cart = await this.fetchJson(this.config.routes.update, {
          method: 'POST',
          body: JSON.stringify({ discount: combinedCodes })
        });

        const discountCheck = await this.waitForPackageDiscount(
          cart,
          discountCode,
          packageInstance,
          Number(this.config.discountPercent || 0)
        );
        cart = discountCheck.cart;

        if (!discountCheck.valid) {
          console.warn('[Cove package] Discount verification failed.', discountCheck.status);
          await this.rollbackPackageItems(cart, discountCode, packageInstance);
          const discountError = new Error(PDP.discountNotAppliedAll || 'The configured Shopify discount code was not applied to every selected package product.');
          discountError.code = 'PACKAGE_DISCOUNT_INVALID';
          throw discountError;
        }
      }

      // Automatic Shopify Function discount: no code is applied, so verify the
      // actual allocations Shopify returned rather than trusting the estimate.
      if (this.config.discountMethod === 'automatic' && this.getDiscountPercent() > 0) {
        const automaticCheck = await this.waitForAutomaticPackageDiscount(cart, packageInstance, desired);
        cart = automaticCheck.cart;

        if (!automaticCheck.valid) {
          console.warn('[Cove package] Automatic package discount verification failed.', automaticCheck.status);
          await this.rollbackPackageItems(cart, '', packageInstance);
          const automaticError = new Error(PDP.discountNotApplied || 'The package discount was not applied correctly to the added items.');
          automaticError.code = 'PACKAGE_DISCOUNT_INVALID';
          throw automaticError;
        }
      }

      if (previousPackageInstance) {
        try {
          cart = await this.removePackageInstance(cart, previousPackageInstance);
        } catch (error) {
          await this.rollbackPackageItems(cart, discountCode, packageInstance);
          throw error;
        }

        this.editPackageInstance = packageInstance;
        const params = new URLSearchParams(window.location.search);
        params.set('edit_package', packageInstance);
        const query = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      }

      return cart;
    }

    lineDiscountAllocation(line) {
      return (line.line_level_discount_allocations || [])
        .reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0);
    }

    // Only allocations produced by an automatic discount count as proof of the
    // package Function. Code-based allocations (for example PACKAGE-10) are
    // deliberately excluded so an unrelated code can never satisfy verification.
    //
    // /cart.js reports Function-generated automatic allocations with an empty
    // discount_application.type, so requiring an explicit "automatic" type
    // rejects every valid allocation. Exclude code-based allocations instead.
    automaticDiscountAllocation(line) {
      return (line.line_level_discount_allocations || []).reduce((sum, allocation) => {
        const type = String(allocation?.discount_application?.type || '').toLowerCase();
        if (type === 'discount_code') return sum;
        return sum + Number(allocation.amount || 0);
      }, 0);
    }

    // /cart.js is authoritative. The submitted payload defines exactly which
    // target lines must exist; empty arrays are never treated as success.
    getAutomaticDiscountStatus(cart, packageInstance, desiredItems) {
      const discountPercent = this.getDiscountPercent();
      // Same signal the displayed total uses: the app publishes per-option
      // prices only when dynamic pricing is on for this bundle.
      const parentPricedDynamically = this.getSimpleBundlesPrice() != null;
      const targetRoles = ['included_extra', 'addon'];
      const allowedRoles = ['included_parent'].concat(targetRoles);

      const expected = (Array.isArray(desiredItems) ? desiredItems : []).reduce((totals, item) => {
        const role = String(item?.properties?._package_role || '');
        if (targetRoles.indexOf(role) === -1) return totals;
        totals.quantity += Number(item.quantity || 0);
        totals.lines += 1;
        return totals;
      }, { quantity: 0, lines: 0 });

      const lines = (cart.items || []).filter((line) => {
        return String(line.properties?._package_instance || '') === String(packageInstance || '');
      });

      const parentLines = lines.filter((line) => String(line.properties?._package_role || '') === 'included_parent');
      const targetLines = lines.filter((line) => targetRoles.indexOf(String(line.properties?._package_role || '')) !== -1);
      const unexpectedRoles = lines.filter((line) => allowedRoles.indexOf(String(line.properties?._package_role || '')) === -1);

      const actualTargetQuantity = targetLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);

      const status = {
        parentCountOk: parentLines.length === 1,
        noUnexpectedRoles: unexpectedRoles.length === 0,
        targetLinesPresent: expected.lines === 0 ? targetLines.length === 0 : targetLines.length > 0,
        targetQuantityOk: actualTargetQuantity === expected.quantity,
        // Under a FIXED parent price the discount is already inside the number,
        // so any allocation on the parent would be a second discount and the add
        // is rejected. Under DYNAMIC pricing the parent line carries the raw sum
        // and the function must take the tier off it, so the same check inverts:
        // the parent has to be discounted, by exactly the tier, or the customer
        // would be charged the undiscounted sum. Both directions are money bugs,
        // so both are verified rather than assumed.
        parentDiscountOk: parentLines.every((line) => {
          const allocated = this.automaticDiscountAllocation(line);
          if (!parentPricedDynamically) return allocated === 0;
          const regular = Number(line.original_line_price ?? line.line_price ?? 0);
          const expectedAmount = Math.round(regular * discountPercent / 100);
          // One minor unit of tolerance for Shopify's own rounding.
          return Math.abs(allocated - expectedAmount) <= 1;
        }),
        // The parent's PRICE, not only the discount on it. Under dynamic
        // pricing Simple Bundles re-prices the parent line from the options
        // carried on it, so the page's raw sum and the cart's raw line price
        // are two INDEPENDENT computations of the same number. When they
        // disagree the customer is being charged for a configuration they did
        // not choose - the failure the 260 cm dining table would produce if the
        // app ever stopped re-pricing: page 40,120, cart 38,820, and the tier
        // check above still passes because 10% of the wrong number is a
        // consistent 10%. Comparing the two numbers is the only check that
        // catches it, so a mismatch rolls the add back instead of leaving an
        // under-priced package in the cart.
        parentPriceOk: parentLines.every((line) => {
          const expectedRaw = this.getSimpleBundlesPrice();
          if (expectedRaw == null) return true;
          const actual = Number(line.original_line_price ?? line.line_price ?? 0);
          // One minor unit of tolerance for Shopify's own rounding.
          return Math.abs(actual - expectedRaw) <= 1;
        }),
        parentPricedDynamically,
        targetsDiscounted: targetLines.every((line) => {
          const regular = Number(line.original_line_price ?? line.line_price ?? 0);
          const expectedAmount = Math.round(regular * discountPercent / 100);
          // One minor unit of tolerance for Shopify's own rounding.
          return Math.abs(this.automaticDiscountAllocation(line) - expectedAmount) <= 1;
        }),
        expectedTargetQuantity: expected.quantity,
        expectedTargetLines: expected.lines,
        actualTargetQuantity,
        actualTargetLines: targetLines.length,
        parentCount: parentLines.length,
        unexpectedRoleCount: unexpectedRoles.length
      };

      status.valid = status.parentCountOk
        && status.noUnexpectedRoles
        && status.targetLinesPresent
        && status.targetQuantityOk
        && status.parentDiscountOk
        && status.parentPriceOk
        && status.targetsDiscounted;

      return status;
    }

    // Shopify recalculates automatic discounts asynchronously, so poll /cart.js
    // a few times before treating a missing allocation as a failure.
    async waitForAutomaticPackageDiscount(initialCart, packageInstance, desiredItems) {
      let cart = initialCart;
      let status = this.getAutomaticDiscountStatus(cart, packageInstance, desiredItems);
      const delays = [250, 500, 900, 1500];

      for (let attempt = 0; attempt < delays.length && !status.valid; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
        try {
          cart = await this.fetchJson(`${this.config.routes.cart}?package_check=${Date.now()}`);
        } catch (error) {
          break;
        }
        status = this.getAutomaticDiscountStatus(cart, packageInstance, desiredItems);
      }

      return { valid: status.valid, status, cart };
    }

    getPackageDiscountStatus(cart, discountCode, packageInstance, discountPercent) {
      const normalizedCode = String(discountCode || '').trim().toLowerCase();
      const packageLines = (cart.items || []).filter((line) => {
        return String(line.properties?._package_instance || '') === String(packageInstance || '');
      });

      const appliedCode = Array.isArray(cart.discount_codes)
        ? cart.discount_codes.find((entry) => String(entry.code || '').trim().toLowerCase() === normalizedCode)
        : null;

      const cartApplications = Array.isArray(cart.cart_level_discount_applications)
        ? cart.cart_level_discount_applications
        : [];
      const applicationMatchesCode = cartApplications.some((application) => {
        const title = String(application?.title || application?.code || '').trim().toLowerCase();
        return title === normalizedCode;
      });

      let originalTotal = 0;
      let finalTotal = 0;
      let allocationTotal = 0;
      let allocationMatchesCode = false;

      packageLines.forEach((line) => {
        const originalLinePrice = Number(line.original_line_price ?? line.line_price ?? 0);
        const finalLinePrice = Number(line.final_line_price ?? line.line_price ?? originalLinePrice);
        originalTotal += Math.max(0, originalLinePrice);
        finalTotal += Math.max(0, finalLinePrice);

        (line.line_level_discount_allocations || []).forEach((allocation) => {
          allocationTotal += Math.max(0, Number(allocation?.amount || 0));
          const application = allocation?.discount_application || {};
          const title = String(
            application.title
            || application.code
            || allocation.title
            || allocation.code
            || ''
          ).trim().toLowerCase();
          if (title === normalizedCode) allocationMatchesCode = true;
        });
      });

      const actualDiscount = Math.max(
        0,
        originalTotal - finalTotal,
        allocationTotal
      );
      const expectedDiscount = Math.round(
        originalTotal * Math.max(0, Math.min(100, Number(discountPercent || 0))) / 100
      );
      const roundingTolerance = Math.max(2, packageLines.length);
      const codeAccepted = Boolean(
        (appliedCode && appliedCode.applicable !== false)
        || applicationMatchesCode
        || allocationMatchesCode
      );

      return {
        valid: packageLines.length > 0
          && codeAccepted
          && actualDiscount + roundingTolerance >= expectedDiscount,
        packageLinesFound: packageLines.length > 0,
        codeAccepted,
        actualDiscount,
        expectedDiscount
      };
    }

    async waitForPackageDiscount(initialCart, discountCode, packageInstance, discountPercent) {
      let cart = initialCart;
      const retryDelays = [0, 150, 350, 700, 1200];

      for (let index = 0; index < retryDelays.length; index += 1) {
        if (retryDelays[index] > 0) {
          await new Promise((resolve) => window.setTimeout(resolve, retryDelays[index]));
          cart = await this.fetchJson(`${this.config.routes.cart}?package_check=${Date.now()}`);
        }

        const status = this.getPackageDiscountStatus(
          cart,
          discountCode,
          packageInstance,
          discountPercent
        );
        if (status.valid) return { cart, valid: true, status };
      }

      return {
        cart,
        valid: false,
        status: this.getPackageDiscountStatus(
          cart,
          discountCode,
          packageInstance,
          discountPercent
        )
      };
    }

    async removePackageInstance(cart, packageInstance) {
      const packageUpdates = {};
      const targetLines = (cart.items || []).filter((line) => {
        return String(line.properties?._package_instance || '') === String(packageInstance || '');
      });

      targetLines.forEach((line) => {
        packageUpdates[line.key] = 0;
      });

      if (!Object.keys(packageUpdates).length) return cart;

      const removedCodes = new Set(
        targetLines
          .map((line) => String(line.properties?._package_discount_code || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const requiredCodes = new Set(
        (cart.items || [])
          .filter((line) => String(line.properties?._package_instance || '') !== String(packageInstance || ''))
          .map((line) => String(line.properties?._package_discount_code || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const remainingCodes = Array.isArray(cart.discount_codes)
        ? cart.discount_codes
          .map((entry) => String(entry.code || '').trim())
          .filter((code) => code && (!removedCodes.has(code.toLowerCase()) || requiredCodes.has(code.toLowerCase())))
        : [];

      return this.fetchJson(this.config.routes.update, {
        method: 'POST',
        body: JSON.stringify({
          updates: packageUpdates,
          discount: remainingCodes.join(',')
        })
      });
    }

    async rollbackPackageItems(cart, discountCode, packageInstance) {
      await this.removePackageInstance(cart, packageInstance).catch(() => {});
    }

    setBusy(busy, action) {
      this.busy = busy;
      const loadingLabel = this.config.labels?.adding || 'ADDING…';

      this.actionButtons.forEach((button) => {
        if (!button.dataset.originalLabel) button.dataset.originalLabel = button.textContent.trim();
        button.disabled = busy;
        button.setAttribute('aria-busy', busy ? 'true' : 'false');
        if (busy) {
          button.textContent = loadingLabel;
        } else {
          const isBuy = button.dataset.packageAction === 'buy';
          const addLabel = this.editPackageInstance
            ? (this.config.labels?.update || PDP.updatePackage || 'UPDATE PACKAGE')
            : (this.config.labels?.add || button.dataset.originalLabel);
          button.textContent = isBuy ? (this.config.labels?.buy || button.dataset.originalLabel) : addLabel;
        }
      });
    }

    async fetchJson(url, options) {
      if (!url) throw new Error(PDP.missingRoute || 'Missing Shopify route.');
      const requestOptions = Object.assign({
        credentials: 'same-origin',
      }, options || {});
      requestOptions.headers = Object.assign({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Cove-Package-Internal': '1'
      }, options?.headers || {});

      const response = await fetch(url, requestOptions);

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.description || payload.message || `Shopify request failed (${response.status}).`);
      }
      return payload;
    }

    notifyCartUpdated(cart) {
      // This is the package add / Update / Remove path. It used to carry its own
      // copy of the package-aware count and the header write; global.js owns
      // both now, so the count cannot differ between this path and the cart page
      // or drawer. syncCartCount() still runs to refresh the drawer's own "(N)".
      window.PovelleaCartCount?.sync(cart);
      window.PovelleaPackageCart?.syncCartCount?.(cart);

      const detail = { cart, source: 'cove-package-builder' };
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
    }

    async openCartDrawer(cart) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return false;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return false;
        const data = await response.json();
        const html = data && data['cart-drawer'];
        const target = document.getElementById('CartDrawer');
        const fresh = html ? new DOMParser().parseFromString(html, 'text/html').getElementById('CartDrawer') : null;
        if (!target || !fresh) return false;
        target.innerHTML = fresh.innerHTML;
        const hasItems = Boolean(cart && Number(cart.item_count) > 0);
        drawer.classList.toggle('is-empty', !hasItems);
        drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', !hasItems);
        const overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') {
          overlay.addEventListener('click', drawer.close.bind(drawer), { once: true });
        }
        drawer.open();
        return true;
      } catch (error) {
        return false;
      }
    }

    showToast(message) {
      if (!this.toast) return;
      if (this.toastTimer) clearTimeout(this.toastTimer);

      this.toast.textContent = message;
      this.toast.hidden = false;
      requestAnimationFrame(() => this.toast?.classList.add('is-visible'));

      this.toastTimer = window.setTimeout(() => {
        this.toast?.classList.remove('is-visible');
        window.setTimeout(() => {
          if (this.toast) this.toast.hidden = true;
        }, 190);
      }, 1800);
    }

    initializeQuickView() {
      const template = this.querySelector('[data-product-modal-template]');
      if (!template?.content?.firstElementChild) return;

      this.modal = template.content.firstElementChild.cloneNode(true);
      ['--cove-accent', '--cove-text', '--cove-card-bg'].forEach((property) => {
        const value = getComputedStyle(this).getPropertyValue(property);
        if (value) this.modal.style.setProperty(property, value);
      });
      document.body.appendChild(this.modal);
      if (this.toast) document.body.appendChild(this.toast);

      this.modalContent = this.modal.querySelector('[data-modal-content]');
      this.modalTrack = this.modal.querySelector('[data-modal-track]');
      this.modalSlideImages = Array.from(this.modal.querySelectorAll('[data-modal-slide-image]'));
      this.modalThumbs = this.modal.querySelector('[data-modal-thumbnails]');
      this.modalTitle = this.modal.querySelector('[data-modal-title]');
      this.modalPrice = this.modal.querySelector('[data-modal-price]');
      this.modalDescription = this.modal.querySelector('[data-modal-description]');
      this.modalAccordions = this.modal.querySelector('[data-modal-accordions]');
      this.modalSwipeArea = this.modal.querySelector('[data-modal-swipe-area]');

      this.listen(this, 'click', (event) => {
        const button = event.target.closest('[data-product-view]');
        if (!button || !this.contains(button)) return;
        const record = button.closest('[data-package-card]')?.__coveRecord;
        if (record) this.openModal(record, button);
      });

      this.listen(this.modal, 'click', (event) => {
        if (event.target === this.modal || event.target.closest('[data-modal-close]')) {
          this.closeModal();
          return;
        }

        const thumb = event.target.closest('[data-modal-thumb]');
        if (thumb) {
          this.modalIndex = Number(thumb.dataset.modalIndex);
          this.renderModalGallery();
          return;
        }

        const accordion = event.target.closest('[data-modal-accordion-button]');
        if (accordion) {
          const panel = accordion.nextElementSibling;
          const open = accordion.getAttribute('aria-expanded') !== 'true';
          accordion.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (panel) panel.hidden = !open;
        }
      });

      this.listen(this.modal.querySelector('[data-modal-previous]'), 'click', () => this.navigateModal(-1));
      this.listen(this.modal.querySelector('[data-modal-next]'), 'click', () => this.navigateModal(1));
      this.listen(this.modal, 'keydown', (event) => this.handleModalKeydown(event));
      this.initializeModalSwipe();
    }

    openModal(record, trigger) {
      if (!this.modal) return;
      if (this.modalCloseTimer) {
        clearTimeout(this.modalCloseTimer);
        this.modalCloseTimer = null;
      }
      this.modalData = record.data;
      this.modalVariant = record.selectedVariant;
      this.modalImages = Array.isArray(record.data.images) ? record.data.images.slice() : [];

      if (!this.modalImages.length) {
        const fallback = record.element.querySelector('.cove-package-card__image')?.currentSrc || record.element.querySelector('.cove-package-card__image')?.src;
        if (fallback) this.modalImages.push({ src: fallback, thumb: fallback, alt: record.data.title || '' });
      }

      const featured = this.modalVariant?.featured_image?.src || this.modalVariant?.featured_image?.url;
      const featuredIndex = featured ? this.modalImages.findIndex((image) => this.sameShopifyImage(image.src, featured)) : -1;
      this.modalIndex = featuredIndex >= 0 ? featuredIndex : 0;
      this.modalLastFocused = trigger || document.activeElement;

      this.modalTitle.textContent = record.data.title || '';
      this.modalPrice.innerHTML = this.formatMoney(Number(this.modalVariant?.price || 0));
      this.modalDescription.textContent = record.data.description || '';

      this.renderModalGallery();
      this.renderModalAccordions(record.data.metafields || {});

      this.lockBody();
      this.modal.hidden = false;
      requestAnimationFrame(() => {
        this.modal?.classList.add('is-open');
        this.modalContent?.focus({ preventScroll: true });
      });
    }

    closeModal(immediate) {
      if (!this.modal || this.modal.hidden) return;
      this.modal.classList.remove('is-open');
      const finish = () => {
        if (!this.modal) return;
        this.modal.hidden = true;
        this.unlockBody();
        this.modalLastFocused?.focus?.({ preventScroll: true });
        this.modalLastFocused = null;
      };

      if (immediate) finish();
      else {
        this.modalCloseTimer = window.setTimeout(() => {
          this.modalCloseTimer = null;
          finish();
        }, 230);
      }
    }

    renderModalGallery() {
      if (!this.modalTrack || !this.modalImages.length) return;
      const count = this.modalImages.length;
      this.modalIndex = ((this.modalIndex % count) + count) % count;
      const positions = [
        (this.modalIndex - 1 + count) % count,
        this.modalIndex,
        (this.modalIndex + 1) % count
      ];

      this.modalTrack.classList.remove('is-animating');
      this.modalTrack.style.transform = 'translate3d(-100%, 0, 0)';

      this.modalSlideImages.forEach((image, slideIndex) => {
        const data = this.modalImages[positions[slideIndex]] || {};
        image.src = data.src || '';
        image.alt = data.alt || this.modalData?.title || '';
        if (data.width) image.width = Number(data.width);
        if (data.height) image.height = Number(data.height);
      });

      this.renderModalThumbnails();
      this.preloadModalNeighbors();
    }

    renderModalThumbnails() {
      if (!this.modalThumbs) return;
      this.modalThumbs.replaceChildren();

      this.modalImages.forEach((image, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `cove-product-modal__thumb${index === this.modalIndex ? ' is-active' : ''}`;
        button.dataset.modalThumb = '';
        button.dataset.modalIndex = String(index);
        button.setAttribute('aria-label', (PDL.showImage || 'Show image __INDEX__').replace('__INDEX__', index + 1));
        button.setAttribute('aria-pressed', index === this.modalIndex ? 'true' : 'false');

        const thumbnail = document.createElement('img');
        thumbnail.src = image.thumb || image.src;
        thumbnail.alt = '';
        thumbnail.width = 120;
        thumbnail.height = 100;
        thumbnail.loading = 'lazy';
        thumbnail.decoding = 'async';
        button.appendChild(thumbnail);
        this.modalThumbs.appendChild(button);
      });

      this.modalThumbs.querySelector('.is-active')?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }

    navigateModal(direction) {
      if (this.modalAnimating || this.modalImages.length < 2) return;
      this.modalAnimating = true;
      this.modalTrack.classList.add('is-animating');
      this.modalTrack.style.transform = direction > 0 ? 'translate3d(-200%, 0, 0)' : 'translate3d(0, 0, 0)';

      const finish = () => {
        this.modalTrack.removeEventListener('transitionend', finish);
        this.modalIndex += direction;
        this.modalAnimating = false;
        this.renderModalGallery();
      };

      this.modalTrack.addEventListener('transitionend', finish, { once: true });
      window.setTimeout(() => {
        if (this.modalAnimating) finish();
      }, 430);
    }

    initializeModalSwipe() {
      if (!this.modalSwipeArea) return;

      this.listen(this.modalSwipeArea, 'pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (this.modalAnimating) return;
        this.modalPointer = {
          id: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          time: performance.now(),
          horizontal: false
        };
        this.modalSwipeArea.setPointerCapture?.(event.pointerId);
      });

      this.listen(this.modalSwipeArea, 'pointermove', (event) => {
        if (!this.modalPointer || event.pointerId !== this.modalPointer.id) return;
        const x = event.clientX - this.modalPointer.startX;
        const y = event.clientY - this.modalPointer.startY;

        if (!this.modalPointer.horizontal && Math.abs(x) > 8) {
          if (Math.abs(y) > Math.abs(x)) return;
          this.modalPointer.horizontal = true;
        }

        if (this.modalPointer.horizontal) {
          event.preventDefault();
          this.modalTrack.classList.remove('is-animating');
          this.modalTrack.style.transform = `translate3d(calc(-100% + ${x}px), 0, 0)`;
        }
      });

      const finishSwipe = (event) => {
        if (!this.modalPointer || event.pointerId !== this.modalPointer.id) return;
        const x = event.clientX - this.modalPointer.startX;
        const elapsed = Math.max(1, performance.now() - this.modalPointer.time);
        const velocity = Math.abs(x) / elapsed;
        const shouldMove = this.modalPointer.horizontal && (Math.abs(x) > 48 || velocity > 0.45);

        this.modalSwipeArea.releasePointerCapture?.(event.pointerId);
        this.modalPointer = null;

        if (shouldMove) {
          this.navigateModal(x < 0 ? 1 : -1);
        } else {
          this.modalTrack.classList.add('is-animating');
          this.modalTrack.style.transform = 'translate3d(-100%, 0, 0)';
        }
      };

      this.listen(this.modalSwipeArea, 'pointerup', finishSwipe);
      this.listen(this.modalSwipeArea, 'pointercancel', finishSwipe);
      this.listen(this.modalSwipeArea, 'dragstart', (event) => event.preventDefault());
    }

    renderModalAccordions(metafields) {
      if (!this.modalAccordions) return;
      this.modalAccordions.replaceChildren();

      const definitions = [
        ['overview', (useI18n && PDA.overview) || 'Overview'],
        ['dimensions_fit', (useI18n && PDA.dimensionsFit) || 'Dimensions & Fit'],
        ['materials_finish', (useI18n && PDA.materialsFinish) || 'Materials & Finish'],
        ['comfort_seating', (useI18n && PDA.comfortSeating) || 'Comfort & Seating'],
        ['care_guide', (useI18n && PDA.careGuide) || 'Care Guide'],
        ['delivery_assembly', (useI18n && PDA.deliveryAssembly) || 'Delivery & Assembly'],
        ['warranty_after_sales', (useI18n && PDA.warrantyAfterSales) || 'Warranty & After Sales'],
        ['whats_included', (useI18n && PDA.whatsIncluded) || 'What’s Included'],
        ['product_highlights', (useI18n && PDA.productHighlights) || 'Product Highlights']
      ];

      definitions.forEach(([key, label]) => {
        const value = this.extractText(metafields[key]);
        if (!value) return;
        // Second gate. The Liquid guard already withholds an unresolved
        // dimension from this config, so reaching here means the value came
        // from somewhere else - a cached section render, or a future caller
        // that forgets the guard. Either way it must not become visible text.
        if (key === 'dimensions_fit' && !isResolvedDimension(value)) return;

        const item = document.createElement('div');
        item.className = 'cove-product-modal__accordion';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cove-product-modal__accordion-button';
        button.dataset.modalAccordionButton = '';
        button.setAttribute('aria-expanded', 'false');
        button.append(document.createTextNode(label));

        const icon = document.createElement('span');
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '+';
        button.appendChild(icon);

        const panel = document.createElement('div');
        panel.className = 'cove-product-modal__accordion-panel';
        panel.hidden = true;
        panel.textContent = value;

        item.append(button, panel);
        this.modalAccordions.appendChild(item);
      });

      this.modalAccordions.hidden = !this.modalAccordions.children.length;
    }

    extractText(value) {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
      if (Array.isArray(value)) return value.map((item) => this.extractText(item)).filter(Boolean).join('\n');
      if (typeof value === 'object') {
        if (typeof value.value === 'string') return value.value.trim();
        if (typeof value.text === 'string') return value.text.trim();
        if (Array.isArray(value.children)) return value.children.map((child) => this.extractText(child)).filter(Boolean).join(' ');
        return Object.values(value).map((item) => this.extractText(item)).filter(Boolean).join('\n');
      }
      return '';
    }

    handleModalKeydown(event) {
      if (!this.modal || this.modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key === 'ArrowLeft' && event.target.closest('[data-modal-gallery]')) {
        event.preventDefault();
        this.navigateModal(-1);
      } else if (event.key === 'ArrowRight' && event.target.closest('[data-modal-gallery]')) {
        event.preventDefault();
        this.navigateModal(1);
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.modal.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')).filter((node) => !node.hidden && node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockBody() {
      if (this.bodyLock) return;
      const scrollY = window.scrollY;
      this.bodyLock = {
        scrollY,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        overflow: document.body.style.overflow
      };
      document.body.classList.add('cove-modal-open');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    unlockBody() {
      if (!this.bodyLock) return;
      const saved = this.bodyLock;
      this.bodyLock = null;
      document.body.classList.remove('cove-modal-open');
      document.body.style.position = saved.position;
      document.body.style.top = saved.top;
      document.body.style.width = saved.width;
      document.body.style.overflow = saved.overflow;
      window.scrollTo(0, saved.scrollY);
    }

    preloadModalNeighbors() {
      if (!this.modalImages.length) return;
      const count = this.modalImages.length;
      [
        this.modalImages[(this.modalIndex + 1) % count]?.src,
        this.modalImages[(this.modalIndex - 1 + count) % count]?.src
      ].filter(Boolean).forEach((src) => {
        if (this.preloadedImages.has(src)) return;
        this.preloadedImages.add(src);
        const image = new Image();
        image.decoding = 'async';
        image.src = src;
      });
    }

    initializeThemeEditor() {
      if (!window.Shopify?.designMode) return;

      this.listen(document, 'shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;

        const slide = event.target.closest('[data-gallery-slide]');
        if (slide) {
          this.goToGallery(Number(slide.dataset.galleryIndex), true);
          return;
        }

        const card = event.target.closest('[data-package-card]');
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        else event.target.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      });
    }

    formatMoney(cents) {
      const format = this.config.moneyFormat || '{{amount}}';
      const token = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
      const amount = Number(cents || 0) / 100;
      let value;

      const delimit = (number, decimals, thousands, decimal) => {
        const fixed = Number(number).toFixed(decimals);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
      };

      switch (token) {
        case 'amount_no_decimals':
          value = delimit(amount, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          value = delimit(amount, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = delimit(amount, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = delimit(amount, 2, "'", '.');
          break;
        default:
          value = delimit(amount, 2, ',', '.');
      }

      const formattedAmount = value.replace(/\.00$/, '');
      return '<span class="uae-dirham-price">' +
        '<span class="uae-dirham-sr-only">AED </span>' +
        '<span>' + formattedAmount + '</span>' +
        '</span>';
    }

    shopifyImageUrl(source, width) {
      try {
        const url = new URL(source, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (error) {
        return source;
      }
    }

    sameShopifyImage(left, right) {
      if (!left || !right) return false;
      const normalize = (value) => String(value).split('?')[0].replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|\d+x\d*|x\d+)\.(\w+)$/, '.$1');
      return normalize(left) === normalize(right);
    }
  }

  if (!customElements.get('cove-package-builder')) {
    customElements.define('cove-package-builder', CovePackageBuilder);
  }

  if (!customElements.get('cove-package-builder-v2')) {
    customElements.define('cove-package-builder-v2', class CovePackageBuilderV2 extends CovePackageBuilder {});
  }
})();
